import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import history from '../../core/utils/history';
import * as maplibregl from 'maplibre-gl';
import type { LngLatLike } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { googleProtocol } from 'maplibre-google-maps';
import constants from '../../core/constants';
import { FeatureCollection, Point, GeoJsonProperties, Feature } from 'geojson';
import { AssetMarkerData, MapMode, MapToolbarButtonType, MapToolbarButtonProps, SliderUnitType, SnailTrailPoint, UnitOfMeasurement, EventType, HeatmapEvent, DeviceTimelineSegment } from '../../types/types';
import { createFeatureCollectionFromAssetMarkerData, createFeatureCollectionFromGeofences, createFeatureCollectionFromHeatmapEvents, createFeatureCollectionFromSnailTrailPoints, createGeofenceCentersFromGeojson, filterSnailTrailPointsForEvents, getBoundsForGeojson, getLngLatFromSnailTrailPoint, getMapStyle, getSnailTrailDirectionalArrowSpacingForZoom, isValidLngLat, loadAssetMarker, loadSnailTrailDirectionalMarkerImage, loadSnailTrailEventIconImage, pickTopmostHitAlpha, prewarmAssetMarkerIcons, safelyInteractWithMap, safelyRemoveMapImage, safelyRemoveMapLayer, safelyRemoveMapSource, showMapPing } from '../../core/utils/mapUtils';
import dayjs from 'dayjs';
import { Button, Dropdown, notification, Slider, Tooltip, Tree } from 'antd';
import { EarthEuropeAfrica, HeatMap_02, Information, Location as LocationIcon, Satellite, Time, TrafficEvent, Video } from '@carbon/icons-react';
import MemoizedDatePicker from '../MemoizedDatePicker';
import { Provider, useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from '../../core/reducers';
import { SELECT_ALL_VALUE } from '../AssetSearchSidebar';
import { setCheckedAssets, setFocusAsset, fetchSnailTrailRequest, setSelectedDate, resetSnailTrail, getMapEventsHeatMapRequest, setDeviceHeatMap, setMapSliderValue } from '../../core/device/deviceActions';
import AssetPopup from '../../components/Map/popups/AssetPopup';
import { createRoot } from 'react-dom/client';
import EventPopup from './popups/EventPopup';
import { fetchGeofencesRequest } from '../../core/geofence/geofenceActions';
import { setDataEventToReportParams } from '../../core/global/globalActions';
import { getEventTypesRequest } from '../../core/asset/assetActions';
import LocationSearch from '../LocationSearch';
import DeviceTimeline, { getDeviceTimelineStatusText } from '../DeviceTimeline';
import { setMapSearchLocationBoundary } from '../../core/video/videoActions';

import './map.scss';

const EASE_TO_DEFAULT_DURATION = 400;
const EASE_TO_DEFAULT_ZOOM = 16;
const JUMP_TO_DEFAULT_ZOOM = 16;
const FIT_BOUNDS_DEFAULT_DURATION = 400;
const FIT_BOUNDS_MAX_ZOOM = 18;
const GEOFENCE_DRAWING_TOOL_MIN_DISTANCE_TO_CLOSE = 12;
const MAP_CAMERA_DEFAULT_PADDING = { 
    top: 60, 
    right: 60, 
    bottom: 60, 
    left: 60,
};
const FILTER_SIDEBAR_HEIGHT = '48px';
const EVENT_TYPES_HEATMAP_SELECT_ALL_VALUE = 'all';
export const PENCIL_CURSOR_SVG =
  `url("data:image/svg+xml;utf8,` +
  `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>` +
    `<path d='M3 14h3l7-7-3-3L3 11v3z' fill='%23fff' stroke='%23161616' stroke-width='1.5' stroke-linejoin='round'/>` +
    `<path d='M10 4l3 3' stroke='%23161616' stroke-width='1.5' stroke-linecap='round'/>` +
  `</svg>") 1 14, auto`;
const ASSET_MARKERS_SOURCE_ID = 'asset-geojson-source-raw';
const ASSET_MARKERS_LAYER_ID = 'asset-markers-layer';
const ASSET_MARKER_HOVER_LAYER_ID = 'asset-markers-hover-layer';
const ASSET_HOVER_RING_LAYER_ID = 'asset-hover-ring';
const SNAIL_TRAIL_LINE_SOURCE_ID = 'snail-trail-source';
const SNAIL_TRAIL_LINE_LAYER_ID = 'snail-trail-layer';
const SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID = 'snail-trail-dot-layer';
const SNAIL_TRAIL_DIRECTIONAL_ARROWS_IMAGE_ID = 'snail-trail-dot-img';
const SNAIL_TRAIL_START_END_SOURCE_ID = 'snail-trail-start-end-source';
const SNAIL_TRAIL_START_END_CIRCLE_LAYER_ID = 'snail-trail-start-end-circle';
const SNAIL_TRAIL_START_END_LABEL_LAYER_ID = 'snail-trail-start-end-label';
const SNAIL_TRAIL_EVENTS_SOURCE_ID = 'snail-trail-events-source';
const SNAIL_TRAIL_EVENTS_ICON_LAYER_ID = 'snail-trail-events-icon-layer';
const SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID = 'snail-trail-event-icon-v2';
const SATELLITE_LABELS_SOURCE_ID = 'google-labels-source';
const SATELLITE_LABELS_LAYER_ID = 'google-labels-layer';
const TRAFFIC_SOURCE_ID = 'google-traffic-source';
const TRAFFIC_LAYER_ID = 'google-traffic-layer';
const GEOFENCE_FILL_SOURCE_ID = 'geofences-fill-source';
const GEOFENCE_FILL_LAYER_ID = 'geofences-fill-layer';
const GEOFENCE_OUTLINE_LAYER_ID = 'geofences-outline-layer';
const GEOFENCE_CENTER_SOURCE_ID = 'geofences-center-source';
const GEOFENCE_CENTER_LAYER_ID = 'geofences-center-layer';
const GEOFENCE_DRAWING_TOOL_LINE_SOURCE_ID = 'geofence-draw-line-source';
const GEOFENCE_DRAWING_TOOL_LINE_LAYER_ID = 'geofence-draw-line-layer';
const GEOFENCE_DRAWING_TOOL_FILL_SOURCE_ID = 'geofence-draw-fill-source';
const GEOFENCE_DRAWING_TOOL_FILL_LAYER_ID = 'geofence-draw-fill-layer';
const GEOFENCE_DRAWING_TOOL_POINTS_SOURCE_ID = 'geofence-draw-points-source';
const GEOFENCE_DRAWING_TOOL_POINTS_LAYER_ID = 'geofence-draw-points-layer';
const VIDEO_DRAWING_TOOL_LINE_SOURCE_ID = 'video-draw-line-source';
const VIDEO_DRAWING_TOOL_LINE_LAYER_ID = 'video-draw-line-layer';
const VIDEO_DRAWING_TOOL_FILL_SOURCE_ID = 'video-draw-fill-source';
const VIDEO_DRAWING_TOOL_FILL_LAYER_ID = 'video-draw-fill-layer';
const VIDEO_DRAWING_TOOL_POINTS_SOURCE_ID = 'video-draw-points-source';
const VIDEO_DRAWING_TOOL_POINTS_LAYER_ID = 'video-draw-points-layer';
const HEATMAP_SOURCE_ID = 'events-heatmap-source';
const HEATMAP_LAYER_ID = 'events-heatmap-layer';
const HEATMAP_EVENTS_ICON_LAYER_ID = 'heatmap-events-icon-layer';

interface DrawingToolInteractionHandler {
    click?: (e: maplibregl.MapMouseEvent) => void;
}

interface MapProps {
    assetMarkers: AssetMarkerData[];
    mapMode: MapMode;
    toolbarEnabled?: boolean;
    filterSidebarIsOpen?: boolean;
    onChangeMapMode?: (mode: MapMode) => void;
    injectedHoveredAsset?: number | null;
    parentIsFetchingData?: boolean;
    onSetTimelineData?: (data: any) => void;
}

const MapComponent: React.FC<MapProps> = ({
    assetMarkers,
    mapMode,
    toolbarEnabled = false,
    filterSidebarIsOpen = false,
    onChangeMapMode = null,
    injectedHoveredAsset = null,
    parentIsFetchingData = false,
    onSetTimelineData = () => {},
}) => {
    const dispatch = useDispatch();
    const store = useStore();
    const [selectedSnailTrailAssetId, setSelectedSnailTrailAssetId] = useState<number | null>(null);
    const selectedSnailTrail = useSelector((state: RootState) => selectedSnailTrailAssetId
        ? (state.device.selectedSnailTrail as unknown as SnailTrailPoint[] || null) 
        : null);
    const selectedDateRaw = useSelector((state: RootState) => state.device.selectedDate);
    const selectedDate = dayjs(mapMode === MapMode.Live 
        ? dayjs() 
        : (selectedDateRaw ?? dayjs()));
    const sliderStepInSeconds = selectedSnailTrailAssetId 
        ? 10 
        : 600;
    const SECONDS_IN_A_DAY = 86400;
    const now = dayjs();
    const secondsSinceMidnight = now.diff(now.startOf("day"), "seconds");
    const sliderMaxSteps = selectedDate.isSame(dayjs(), 'day')
        ? Math.floor(secondsSinceMidnight / sliderStepInSeconds)
        : Math.floor(SECONDS_IN_A_DAY / sliderStepInSeconds);
    const [sliderValue, setSliderValue] = useState<number>(sliderMaxSteps);
    const timelineWidth = sliderMaxSteps * sliderStepInSeconds;
    const [sliderScaleX, setSliderScaleX] = useState<number>(1);
    const measureTimelineScale = React.useCallback(() => {
        const rail = document.querySelector('.ant-slider-rail') as HTMLElement | null;
        const w = rail?.getBoundingClientRect().width ?? 1;
        setSliderScaleX(w / (timelineWidth || 1));
    }, [timelineWidth]);
    const [timelineData, setTimelineData] = useState<DeviceTimelineSegment[] | null>(null);
    const geofences = useSelector((state: RootState) => state.geofence.geofences);
    const eventTypes = useSelector((state: RootState) => (state.asset?.event_types || []) as unknown as EventType[]);
    const eventTypesHeatmapTreeData = useMemo(() => [{
        title: 'All Event Types',
        key: EVENT_TYPES_HEATMAP_SELECT_ALL_VALUE,
        children: (eventTypes || []).map((et) => ({
            title: et.key,
            key: String(et.id),
        })),
    }], [eventTypes]);
    const eventTypesHeatmapData = useSelector((state: RootState) => (state.device.mapDeviceHeatMap || []) as unknown as HeatmapEvent[]);
    const eventTypesHeatmapGeojson = useMemo(() => createFeatureCollectionFromHeatmapEvents(eventTypesHeatmapData), [eventTypesHeatmapData]);
    const [assetMarkerNamesActive, setAssetMarkerNamesActive] = useState<boolean>(false);
    const [trafficLayerActive, setTrafficLayerActive] = useState<boolean>(false);
    const [satelliteModeActive, setSatelliteModeActive] = useState<boolean>(false);
    const [geofenceLayerActive, setGeofenceLayerActive] = useState<boolean>(false);
    const [geofenceDrawingToolActive, setGeofenceDrawingToolActive] = useState(false);
    const [videoDrawingToolActive, setVideoDrawingToolActive] = useState(false);
    const [locationSearchActive, setLocationSearchActive] = useState<boolean>(false);
    const telematicsRetentionDays = useSelector((state: RootState) => (state.user.userCompany.telematics_retention_days || 30) as number);
    const user = useSelector((state: RootState) => state.user as any);
    const uomPref: UnitOfMeasurement = user?.profile?.unit_of_measurement_preference || UnitOfMeasurement.BritishImperial;
    const focusAsset = useSelector((state: RootState) => state.device.focusAsset || 0 as number);
    const checkedAssets = useSelector((state: RootState) => (state.device.checkedAssets) as string[]);
    const checkedAssetsForCompare = useMemo<number[]>(() => {
        if (checkedAssets.length > 0 && checkedAssets[0] === SELECT_ALL_VALUE) {
            return assetMarkers.map((am) => am.asset_id);
        } else {
            return checkedAssets
                .map((ca: string) => {
                    const [type, id] = ca.split('-');
                    return type === 'asset' 
                        ? parseInt(id) 
                        : undefined;
                })
                .filter((ca) => ca !== undefined) as number[];
        }
    }, [checkedAssets, assetMarkers]);
    const markersGeojson = useMemo<FeatureCollection<Point, GeoJsonProperties>>(() => {
        const checkedAssetMarkers = assetMarkers.filter((am) =>
            checkedAssetsForCompare.includes(am.asset_id)
        );
        if (!checkedAssetMarkers || checkedAssetMarkers.length === 0) {
            return { 
                type: 'FeatureCollection', 
                features: [],
            };
        } else {
            return createFeatureCollectionFromAssetMarkerData(checkedAssetMarkers, sliderValue, mapMode);
        }
    }, [assetMarkers, checkedAssetsForCompare, sliderValue, mapMode]);
    const mapRef = useRef<maplibregl.Map | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const assetMarkersLngLatByIdRef = useRef<Map<number, [number, number]>>(new Map());
    const markersGeojsonRef = useRef<FeatureCollection<Point, GeoJsonProperties>>({
        type: 'FeatureCollection',
        features: [],
    }); // used for event listeners to pull from an up to date src
    const assetMarkerPopupRef = useRef<maplibregl.Popup | null>(null);
    const eventMarkerPopupRef = useRef<maplibregl.Popup | null>(null);
    const zoomToRequestedAnimationFrameRef = useRef<number | null>(null);
    const shouldZoomToRef = useRef<boolean>(true);
    const shouldZoomToGeofencesRef = useRef<boolean>(false);
    const userIsInteractingRef = useRef<boolean>(false);
    const mapIsMovingRef = useRef<boolean>(false);
    const mapAlreadyInMotionRef = useRef<boolean>(false);
    const hoveredAssetIdRef = useRef<number | null>(null);
    const hasInitializedSatelliteStyleRef = useRef(false);
    const geofenceDraftRef = useRef<[number, number][]>([]);
    const geofenceDrawingToolHandlersRef = useRef<DrawingToolInteractionHandler | null>(null);
    const geofenceDrawingToolActiveRef = useRef(false);
    const videoDrawingToolActiveRef = useRef(false);
    const videoDraftRef = useRef<[number, number][]>([]);
    const videoDrawingToolHandlersRef = useRef<DrawingToolInteractionHandler | null>(null);
    const filterSidebarWidth = filterSidebarIsOpen ? '360px' : FILTER_SIDEBAR_HEIGHT;

    /**
     * Renders everything the map should display. Combined here for convenience when re-rendering e.g. after a map style change.
     * @param map the current value of your map ref
     */
    const renderMapFeatures = (map: maplibregl.Map) => {
        renderSatelliteLabelsOverlay(map);
        setSatelliteLabelsOverlayVisibility(map, satelliteModeActive);
        if (selectedSnailTrail?.length) {
            renderSnailTrailLayers(map);
        }
        renderTrafficLayer(map);
        setTrafficOverlayLayerVisibility(map, trafficLayerActive);
        renderMarkerLayers(map);
        renderHeatmapLayers(map);
        if (geofenceLayerActive) {
            renderGeofenceLayers(map);
        }
        if (geofenceDrawingToolActive) {
            renderGeofenceDrawingToolLayers(map);
        }
        if (videoDrawingToolActive) {
            renderVideoDrawingToolLayers(map);
        }
    }
    /**
     * Render the asset markers with popups
     * @param map the current value of your map ref
     */
    const renderMarkerLayers = (map: maplibregl.Map) => {
        if (!map.getSource(ASSET_MARKERS_SOURCE_ID)) {
            map.addSource(ASSET_MARKERS_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
            } as any);
        }
        if (!map.getLayer(ASSET_MARKERS_LAYER_ID)) {
            map.addLayer({
                id: ASSET_MARKERS_LAYER_ID,
                type: 'symbol',
                source: ASSET_MARKERS_SOURCE_ID,
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': ['get', 'icon_id'],
                    'icon-size': 1.6,
                    'icon-rotate': 0,
                    'icon-rotation-alignment': 'viewport',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'symbol-sort-key': ['to-number', ['get', 'sort_index']],
                    'symbol-z-order': 'source',
                },
            });
        }
        if (!map.getLayer(ASSET_MARKER_HOVER_LAYER_ID)) {
            map.addLayer({
                id: ASSET_MARKER_HOVER_LAYER_ID,
                type: 'symbol',
                source: ASSET_MARKERS_SOURCE_ID,
                filter: ['==', ['to-number', ['get', 'asset_id']], -1],
                layout: {
                    'icon-image': ['coalesce', ['get', 'icon_id_ring'], ['get', 'icon_id_pill'], ['get', 'icon_id']],
                    'icon-size': 1.6,
                    'icon-rotation-alignment': 'viewport',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'symbol-z-order': 'source',
                    'symbol-sort-key': 1e12,
                    'icon-rotate': 0,
                },
            });
            map.moveLayer(ASSET_MARKER_HOVER_LAYER_ID, undefined);
        }
        if (!map.getLayer(ASSET_HOVER_RING_LAYER_ID)) {
            map.addLayer({
                id: ASSET_HOVER_RING_LAYER_ID,
                type: 'circle',
                source: ASSET_MARKERS_SOURCE_ID,
                filter: ['==', ['to-number', ['get', 'asset_id']], -1],
                paint: {
                    'circle-color': '#000',
                    'circle-opacity': 0,
                    'circle-stroke-color': '#1a73e8',
                    'circle-stroke-width': 3,
                    'circle-pitch-scale': 'viewport',
                    'circle-radius': 12,
                },
            });
            map.moveLayer(ASSET_HOVER_RING_LAYER_ID);
        }
        if (!(map as any).__assetClickBound) {
            const fetchSnailTrail = (e: maplibregl.MapLayerMouseEvent) => {
                const f = pickTopmostHitAlpha(map, e.point, ASSET_MARKERS_LAYER_ID);
                if (!f) {
                    return;
                }
                const p = f.properties || {};
                dispatch(resetSnailTrail());
                setMapMode(MapMode.Historic, [`asset-${p.asset_id}`]);
                setSelectedSnailTrailAssetId(Number(p.asset_id));
                const imei = (p as any).imei as string | undefined;
                if (imei) {
                    dispatch(fetchSnailTrailRequest({
                        date: selectedDate.format('YYYY-MM-DD'),
                        imei,
                        slider_unit_type: SliderUnitType.Seconds,
                        slider_unit_size: 10,
                    }));
                }
            };
            map.on('click', ASSET_MARKERS_LAYER_ID, fetchSnailTrail);
            (map as any).__assetClickBound = true;
        }
        if (!(map as any).__assetHoverBound) {
            const state = { raf: 0 as number | 0 };
            const hoverState = {
                closeTimer: 0 as number | 0,
                openTimer: 0 as number | 0,
                pending: null as null | {
                    id: number;
                    lngLat: maplibregl.LngLat;
                    coords: [number, number];
                    dvrImei: string;
                    lastRecordId: number;
                },
                popupMouseOver: false,
                popupRoot: null as ReturnType<typeof createRoot> | null,
                containerEl: null as HTMLDivElement | null,
            };
            const clearCloseTimer = () => {
                if (hoverState.closeTimer) {
                    window.clearTimeout(hoverState.closeTimer);
                    hoverState.closeTimer = 0 as number | 0;
                }
            };
            const clearOpenTimer = () => {
                if (hoverState.openTimer) {
                    window.clearTimeout(hoverState.openTimer);
                    hoverState.openTimer = 0 as number | 0;
                }
                hoverState.pending = null;
            };
            const destroyPopup = () => {
                clearCloseTimer();
                clearOpenTimer();
                if (assetMarkerPopupRef.current) {
                    assetMarkerPopupRef.current.remove();
                    assetMarkerPopupRef.current = null;
                }
                setHoveredAsset(map, null);
                hoverState.popupRoot?.unmount();
                hoverState.popupRoot = null;
                hoverState.containerEl = null;
            };
            const scheduleClose = () => {
                clearCloseTimer();
                hoverState.closeTimer = window.setTimeout(() => {
                    if (!hoverState.popupMouseOver) {
                        destroyPopup();
                    }
                }, 300) as unknown as number;
            };
            const attachPopup = () => {
                const el = assetMarkerPopupRef.current?.getElement?.();
                if (!el) {
                    return requestAnimationFrame(attachPopup);
                }
                el.addEventListener('mouseenter', () => {
                    hoverState.popupMouseOver = true;
                    clearCloseTimer();
                });
                el.addEventListener('mouseleave', () => {
                    hoverState.popupMouseOver = false;
                    scheduleClose();
                });
            };
            const scheduleOpen = (f: maplibregl.MapGeoJSONFeature, eLngLat: maplibregl.LngLat) => {
                const p: any = f.properties || {};
                const assetId = Number(p.asset_id);
                const dvrImei = p.dvr_imei != null 
                    ? String(p.dvr_imei) 
                    : "";
                const lastRecordId = p.last_record_id != null 
                    ? Number(p.last_record_id) 
                    : 0;
                let coords = (f.geometry as GeoJSON.Point)?.coordinates?.slice?.() as [number, number] | undefined;
                if (!coords) {
                    return;
                }
                while (Math.abs(eLngLat.lng - coords[0]) > 180) {
                    coords[0] += eLngLat.lng > coords[0] ? 360 : -360;
                }
                if (hoverState.pending?.id !== assetId) {
                    clearOpenTimer();
                    hoverState.pending = { 
                        id: assetId, 
                        lngLat: eLngLat, 
                        coords, 
                        dvrImei, 
                        lastRecordId, 
                    };
                }
                if (!hoverState.openTimer) {
                    hoverState.openTimer = window.setTimeout(() => {
                        const pending = hoverState.pending;
                        hoverState.openTimer = 0 as number | 0;
                        hoverState.pending = null;
                        if (!pending) {
                            return;
                        }
                        clearCloseTimer();
                        if (!assetMarkerPopupRef.current) {
                            assetMarkerPopupRef.current = new maplibregl.Popup({
                                closeButton: false,
                                closeOnClick: false,
                                offset: 16,
                                className: 'asset-popup-base',
                                maxWidth: 'none',
                            } as any).addTo(map);
                            attachPopup();
                        }
                        const prevId = hoveredAssetIdRef.current;
                        setHoveredAsset(map, pending.id);
                        if (prevId !== pending.id || !hoverState.containerEl) {
                            const container = document.createElement('div');
                            container.style.width = '320px';
                            container.style.minHeight = '200px';
                            if (prevId !== pending.id) {
                                hoverState.popupRoot?.unmount();
                                hoverState.popupRoot = null;
                            }
                            hoverState.containerEl = container;
                            if (!hoverState.popupRoot) {
                                hoverState.popupRoot = createRoot(container);
                            }
                            hoverState.popupRoot.render(
                                <Provider store={store}>
                                    <AssetPopup
                                        assetId={pending.id}
                                        date={selectedDate.format('YYYY-MM-DD')}
                                        uomPref={uomPref}
                                        dvrImei={pending.dvrImei}
                                        recordId={pending.lastRecordId}
                                    />
                                </Provider>
                            );
                            assetMarkerPopupRef.current!.setDOMContent(container);
                        } else {
                            assetMarkerPopupRef.current!.setDOMContent(hoverState.containerEl);
                        }
                        assetMarkerPopupRef.current!.setLngLat(pending.coords);
                    }, 200) as unknown as number;
                }
            };
            const onEnter = () => {
                map.getCanvas().style.cursor = 'pointer';
                clearCloseTimer();
            };
            const onLeave = () => {
                map.getCanvas().style.cursor = (geofenceDrawingToolActiveRef.current || videoDrawingToolActiveRef.current ? PENCIL_CURSOR_SVG : '');
                clearOpenTimer();
                scheduleClose();
            };
            const onMove = (e: maplibregl.MapLayerMouseEvent) => {
                if (state.raf) return;
                state.raf = requestAnimationFrame(() => {
                    state.raf = 0;
                    const top = pickTopmostHitAlpha(map, e.point, ASSET_MARKERS_LAYER_ID);
                    map.getCanvas().style.cursor = top ? 'pointer' : (geofenceDrawingToolActiveRef.current || videoDrawingToolActiveRef.current ? PENCIL_CURSOR_SVG : '');
                    if (!top) {
                        clearOpenTimer();
                        scheduleClose();
                        return;
                    }
                    clearCloseTimer();
                    const nextId = Number((top.properties as any)?.asset_id);
                    if (hoveredAssetIdRef.current !== nextId || !assetMarkerPopupRef.current) {
                        scheduleOpen(top, e.lngLat);
                    } else {
                        const coords = (top.geometry as GeoJSON.Point)?.coordinates as [number, number];
                        assetMarkerPopupRef.current?.setLngLat(coords);
                    }
                });
            };
            map.on('mouseenter', ASSET_MARKERS_LAYER_ID, onEnter);
            map.on('mouseleave', ASSET_MARKERS_LAYER_ID, onLeave);
            map.on('mousemove', ASSET_MARKERS_LAYER_ID, onMove);
            (map as any).__assetHoverBound = { 
                onEnter, 
                onLeave, 
                onMove,
            };
        }
        prewarmAssetMarkerIcons(map, markersGeojson as any, assetMarkerNamesActive);
        for (const f of (markersGeojson.features as Feature<Point, GeoJsonProperties>[])) {
            const p: any = f.properties;
            if (p?.division_color && p?.arrow_color) {
                const angle = Number(p.angle) || 0;
                const assetLabel = (assetMarkerNamesActive && (p.asset_name || p.name || p.label || p.registration || '')) || '';
                const ringId = loadAssetMarker(map, p.division_color, p.arrow_color, angle, false);
                const pillId = loadAssetMarker(map, p.division_color, p.arrow_color, angle, true, 0, assetLabel);
                p.icon_id_ring = ringId;
                p.icon_id_pill = pillId;
                p.icon_id = assetMarkerNamesActive 
                    ? pillId 
                    : ringId;
                const am = assetMarkers.find(am => am.asset_id === Number(p.asset_id));
                if (am?.imei) {
                    p.imei = am.imei;
                }
            }
        }
        (map.getSource(ASSET_MARKERS_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)
            ?.setData(markersGeojson as any);
    }
    /**
     * Render the snail trail and it's various markers, events are rendered seperately in the DOM to support dynamic icons
     * @param map the current value of your map ref
     */
    const renderSnailTrailLayers = (map: maplibregl.Map) => {
        if (!map.getSource(SNAIL_TRAIL_LINE_SOURCE_ID)) {
            map.addSource(SNAIL_TRAIL_LINE_SOURCE_ID, { 
                type: 'geojson', 
                data: { 
                    type: 'FeatureCollection', 
                    features: [], 
                } 
            } as any);
        }
        if (!map.getSource(SNAIL_TRAIL_START_END_SOURCE_ID)) {
            map.addSource(SNAIL_TRAIL_START_END_SOURCE_ID, { 
                type: 'geojson', 
                data: { 
                    type: 'FeatureCollection', 
                    features: [], 
                } 
            } as any);
        }
        if (!map.getSource(SNAIL_TRAIL_EVENTS_SOURCE_ID)) {
            map.addSource(SNAIL_TRAIL_EVENTS_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
            } as any);
        }
        loadSnailTrailDirectionalMarkerImage(map, SNAIL_TRAIL_DIRECTIONAL_ARROWS_IMAGE_ID);
        loadSnailTrailEventIconImage(map, SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID);
        if (!map.getLayer(SNAIL_TRAIL_LINE_LAYER_ID)) {
            map.addLayer({
                id: SNAIL_TRAIL_LINE_LAYER_ID,
                type: 'line',
                source: SNAIL_TRAIL_LINE_SOURCE_ID,
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': '#1a73e8',
                    'line-width': 4,
                    'line-opacity': 0.9
                },
            });
        }
        if (!map.getLayer(SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID)) {
            map.addLayer({
                id: SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID,
                type: 'symbol',
                source: SNAIL_TRAIL_LINE_SOURCE_ID,
                layout: {
                    'symbol-placement': 'line',
                    'symbol-spacing': 280,
                    'icon-image': SNAIL_TRAIL_DIRECTIONAL_ARROWS_IMAGE_ID,
                    'icon-size': 1,
                    'icon-rotation-alignment': 'map',
                    'icon-keep-upright': true,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                },
                paint: {
                    'icon-opacity': 1,
                },
            });
            map.moveLayer(SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID);
        }
        if (!map.getLayer(SNAIL_TRAIL_START_END_CIRCLE_LAYER_ID)) {
            map.addLayer({
                id: SNAIL_TRAIL_START_END_CIRCLE_LAYER_ID,
                type: 'circle',
                source: SNAIL_TRAIL_START_END_SOURCE_ID,
                paint: {
                    'circle-color': '#1a73e8',
                    'circle-radius': 12,
                    'circle-stroke-color': constants.GOOGLE_PATH_BLUE,
                    'circle-stroke-width': 0,
                },
            });
        }
        if (!map.getLayer(SNAIL_TRAIL_START_END_LABEL_LAYER_ID)) {
            map.addLayer({
                id: SNAIL_TRAIL_START_END_LABEL_LAYER_ID,
                type: 'symbol',
                source: SNAIL_TRAIL_START_END_SOURCE_ID,
                layout: {
                    'text-field': ['get', 'label'],
                    'text-size': 10,
                    'text-font': ['Open Sans Regular','Arial Unicode MS Regular'],
                    'text-allow-overlap': true,
                    'text-ignore-placement': true,
                },
                paint: {
                    'text-color': '#fff',
                },
            });
        }
        if (!map.getLayer(SNAIL_TRAIL_EVENTS_ICON_LAYER_ID) && map.hasImage(SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID)) {
            map.addLayer({
                id: SNAIL_TRAIL_EVENTS_ICON_LAYER_ID,
                type: 'symbol',
                source: SNAIL_TRAIL_EVENTS_SOURCE_ID,
                layout: {
                    'icon-image': SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID,
                    'icon-size': 1.0,
                    'icon-rotation-alignment': 'viewport',
                    'icon-allow-overlap': false,
                    'text-allow-overlap': false,
                    'symbol-z-order': 'auto',
                    'symbol-sort-key': ['to-number', ['get', 'sort_key']],
                },
                paint: {},
            });
        }
        const anchor = map.getLayer(ASSET_MARKERS_LAYER_ID) ? ASSET_MARKERS_LAYER_ID : undefined;
        if (map.getLayer(SNAIL_TRAIL_LINE_LAYER_ID)) {
            map.moveLayer(SNAIL_TRAIL_LINE_LAYER_ID, anchor);
        }
        if (map.getLayer(SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID)) {
            map.moveLayer(SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID, anchor);
        }
        if (map.getLayer(SNAIL_TRAIL_START_END_CIRCLE_LAYER_ID)) {
            map.moveLayer(SNAIL_TRAIL_START_END_CIRCLE_LAYER_ID, anchor);
        }
        if (map.getLayer(SNAIL_TRAIL_START_END_LABEL_LAYER_ID)) {
            map.moveLayer(SNAIL_TRAIL_START_END_LABEL_LAYER_ID, anchor);
        }
        if (map.getLayer(SNAIL_TRAIL_EVENTS_ICON_LAYER_ID)) {
            map.moveLayer(SNAIL_TRAIL_EVENTS_ICON_LAYER_ID, anchor);
        }
        if (selectedSnailTrail && selectedSnailTrail.length) {
            const lineSrc = map.getSource(SNAIL_TRAIL_LINE_SOURCE_ID) as maplibregl.GeoJSONSource;
            lineSrc.setData(createFeatureCollectionFromSnailTrailPoints(selectedSnailTrail) as any);
            const startEndSrc = map.getSource(SNAIL_TRAIL_START_END_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
            if (startEndSrc) {
                const features: GeoJSON.Feature<GeoJSON.Point, GeoJsonProperties>[] = [];
                const startLngLat = getLngLatFromSnailTrailPoint(selectedSnailTrail[0]);
                const endLngLat = getLngLatFromSnailTrailPoint(selectedSnailTrail[selectedSnailTrail.length - 1]);
                if (startLngLat) {
                    features.push({
                        type: 'Feature',
                        geometry: { 
                            type: 'Point', 
                            coordinates: startLngLat, 
                        },
                        properties: { label: 'Start' },
                    });
                }
                if (endLngLat) {
                    features.push({
                        type: 'Feature',
                        geometry: { 
                            type: 'Point', 
                            coordinates: endLngLat, 
                        },
                        properties: { label: 'End' },
                    });
                }
                startEndSrc.setData({ 
                    type: 'FeatureCollection', 
                    features, 
                } as any);
            }
            if (map.getLayer(SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID)) {
                const snailTrailDirectionalArrowSpacingForZoom = getSnailTrailDirectionalArrowSpacingForZoom(map.getZoom());
                map.setLayoutProperty(SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID, 'symbol-spacing', snailTrailDirectionalArrowSpacingForZoom);
            }
            const snailTrailEvents = filterSnailTrailPointsForEvents(selectedSnailTrail);
            const snailTrailEventsFeatures = snailTrailEvents.map((stp: SnailTrailPoint, i) => ({
                type: 'Feature',
                geometry: { 
                    type: 'Point', 
                    coordinates: [Number(stp.lng), Number(stp.lat)], 
                },
                properties: {
                    speed: stp.speed,
                    speed_unit: stp.speed_unit,
                    event_label: stp.event_label,
                    time: stp.time,
                    sort_key: i,
                    event_id: stp.event_id,
                    device_id: stp.device_id,
                },
            }));
            (map.getSource(SNAIL_TRAIL_EVENTS_SOURCE_ID) as maplibregl.GeoJSONSource).setData({ 
                type: 'FeatureCollection', 
                features: snailTrailEventsFeatures,
            } as any);
        } else {
            (map.getSource(SNAIL_TRAIL_EVENTS_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData({ 
                type: 'FeatureCollection', 
                features: [], 
            } as any);
        }
        if (!(map as any).__snailTrailEventClickBound) {
            map.on('click', SNAIL_TRAIL_EVENTS_ICON_LAYER_ID, (e: maplibregl.MapLayerMouseEvent) => {
                const feature = e.features?.[0];
                if (!feature) {
                    return;
                }
                const p = feature.properties || {};
                if (p.event_id && p.device_id) {
                    history.push(`/events/custom-view/${p.event_id}/${p.device_id}`);
                }
            });
            (map as any).__snailTrailEventClickBound = true;
        }
        if (!(map as any).__snailTrailEventHoverBound) {
            const state = { raf: 0 as number | 0 };
            let openTimer: number | 0 = 0 as number | 0;
            let closeTimer: number | 0 = 0 as number | 0;
            let popupMouseOver = false;
            let popupRoot: ReturnType<typeof createRoot> | null = null;
            const clearCloseTimer = () => { 
                if (closeTimer) { 
                    window.clearTimeout(closeTimer as any); 
                    closeTimer = 0 as number | 0; 
                } 
            };
            const clearOpenTimer = () => { 
                if (openTimer) { 
                    window.clearTimeout(openTimer as any); 
                    openTimer = 0 as number | 0; 
                } 
            };
            const scheduleClose = () => {
                clearCloseTimer();
                if (!eventMarkerPopupRef.current) {
                    return;
                }
                const popup = eventMarkerPopupRef.current;
                closeTimer = window.setTimeout(() => {
                    if (!popupMouseOver && eventMarkerPopupRef.current === popup) {
                        try { 
                            popup.remove(); 
                        } catch {}
                        popupRoot?.unmount();
                        popupRoot = null;
                        eventMarkerPopupRef.current = null;
                    }
                }, 300) as unknown as number;
            };
            const attachPopupGuards = (popup: maplibregl.Popup) => {
                const el = popup.getElement?.();
                if (!el) {
                    return requestAnimationFrame(() => attachPopupGuards(popup));
                }
                el.style.zIndex = String(21 + 2);
                el.addEventListener('mouseenter', () => { 
                    popupMouseOver = true; 
                    clearCloseTimer(); 
                });
                el.addEventListener('mouseleave', () => { 
                    popupMouseOver = false; 
                    scheduleClose(); 
                });
            };
            const openPopup = (coords: [number, number], props: any) => {
                clearOpenTimer(); 
                clearCloseTimer();
                if (eventMarkerPopupRef.current) { 
                    try { 
                        eventMarkerPopupRef.current.remove(); 
                    } catch {} 
                    eventMarkerPopupRef.current = null; 
                }
                const container = document.createElement('div');
                const selectedAssetMarker = assetMarkers.find(val => val.asset_id === selectedSnailTrailAssetId);
                const driverId = selectedAssetMarker?.driver_id ?? -1;
                const driverName = selectedAssetMarker?.driver_name ?? 'Unknown Driver';
                popupRoot?.unmount();
                popupRoot = createRoot(container);
                popupRoot.render(
                    <EventPopup
                        speed={props.speed}
                        speedUnit={props.speed_unit}
                        eventLabel={props.event_label}
                        time={props.time}
                        driverId={driverId}
                        driverName={driverName}
                        location={{ 
                            lat: Number(coords[1].toFixed(5)), 
                            lng: Number(coords[0].toFixed(5)), 
                        }}
                        uomPref={uomPref}
                    />
                );
                const popup = new maplibregl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    anchor: 'bottom',
                    offset: [0, -16] as maplibregl.PointLike,
                    className: 'event-popup-base',
                    maxWidth: 'auto',
                })
                    .setLngLat(coords)
                    .setDOMContent(container)
                    .addTo(map);
                eventMarkerPopupRef.current = popup;
                attachPopupGuards(popup);
                popup.on('close', () => {
                    clearCloseTimer();
                    popupRoot?.unmount();
                    popupRoot = null;
                    if (eventMarkerPopupRef.current === popup) {
                        eventMarkerPopupRef.current = null;
                    }
                    popupMouseOver = false;
                });
            };
            const onEnter = () => { 
                map.getCanvas().style.cursor = 'pointer'; 
                clearCloseTimer(); 
            };
            const onLeave = () => { 
                map.getCanvas().style.cursor = (geofenceDrawingToolActiveRef.current || videoDrawingToolActiveRef.current ? PENCIL_CURSOR_SVG : ''); 
                clearOpenTimer(); 
                scheduleClose(); 
            };
            const onMove = (e: maplibregl.MapLayerMouseEvent) => {
                if (state.raf) {
                    return;
                }
                state.raf = requestAnimationFrame(() => {
                    state.raf = 0;
                    const f = map.queryRenderedFeatures(e.point, { layers: [ SNAIL_TRAIL_EVENTS_ICON_LAYER_ID ]})?.[0];
                    if (!f) {
                        clearOpenTimer();
                        scheduleClose();
                        map.getCanvas().style.cursor = (geofenceDrawingToolActiveRef.current || videoDrawingToolActiveRef.current ? PENCIL_CURSOR_SVG : '');
                        return;
                    }
                    clearCloseTimer();
                    map.getCanvas().style.cursor = 'pointer';
                    const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
                    if (!eventMarkerPopupRef.current) {
                        clearOpenTimer();
                        openTimer = window.setTimeout(() => {
                            openPopup(coords, f.properties || {});
                            openTimer = 0 as number | 0;
                        }, 200) as unknown as number;
                    } else {
                        try { 
                            eventMarkerPopupRef.current.setLngLat(coords); 
                        } catch {}
                    }
                });
            };
            map.on('mouseenter', SNAIL_TRAIL_EVENTS_ICON_LAYER_ID, onEnter);
            map.on('mouseleave', SNAIL_TRAIL_EVENTS_ICON_LAYER_ID, onLeave);
            map.on('mousemove', SNAIL_TRAIL_EVENTS_ICON_LAYER_ID, onMove);

            (map as any).__snailTrailEventHoverBound = { 
                onEnter, 
                onLeave, 
                onMove, 
            };
        }
    }
    /**
     * Render the traffic overlay layer
     * @param map the current value of your map ref
     */
    const renderTrafficLayer = (map: maplibregl.Map) => {
        if (!map.getSource(TRAFFIC_SOURCE_ID)) {
            const urlTemplate = `https://api.tomtom.com/traffic/map/4/tile/flow/relative/{z}/{x}/{y}.png?tileSize=256&key=${constants.TOMTOM_API_KEY}`;
            try {
                map.addSource(TRAFFIC_SOURCE_ID, {
                    type: 'raster',
                    tiles: [urlTemplate],
                    tileSize: 256,
                    maxzoom: 20,
                    attribution: 'Traffic \u00a9 TomTom',
                } as any);
            } catch (e) {}
        }
        if (!map.getLayer(TRAFFIC_LAYER_ID)) {
            try {
                map.addLayer({
                    id: TRAFFIC_LAYER_ID,
                    type: 'raster',
                    source: TRAFFIC_SOURCE_ID,
                    paint: { 
                        'raster-opacity': 0.85,
                        'raster-fade-duration': 0,
                    },
                    layout: { visibility: 'none' },
                });
            } catch (e) {}
        }
        if (map.getLayer(ASSET_MARKERS_LAYER_ID) && map.getLayer(TRAFFIC_LAYER_ID)) {
            map.moveLayer(TRAFFIC_LAYER_ID, ASSET_MARKERS_LAYER_ID);
        }
    };
    /**
     * Render the labels for the map to be used in satellite mode, as MapLibre only supports raw satellitle imagery without labels
     * e.g. hybrid map type
     * @param map the current value of your map ref
     */
    const renderSatelliteLabelsOverlay = (map: maplibregl.Map) => {
        const urlTemplate = `https://mt1.googleapis.com/vt?lyrs=h&x={x}&y={y}&z={z}&key=${constants.GOOGLE_MAP_API_KEY}`;
        if (!map.getSource(SATELLITE_LABELS_SOURCE_ID)) {
            map.addSource(SATELLITE_LABELS_SOURCE_ID, {
                type: 'raster',
                tiles: [urlTemplate],
                tileSize: 256,
                maxzoom: 20,
            } as any);
        }
        if (!map.getLayer(SATELLITE_LABELS_LAYER_ID) && satelliteModeActive) {
            map.addLayer({
                id: SATELLITE_LABELS_LAYER_ID,
                type: 'raster',
                source: SATELLITE_LABELS_SOURCE_ID,
                paint: { 'raster-opacity': 1 },
            });
            if (map.getLayer(ASSET_MARKERS_LAYER_ID)) {
                map.moveLayer(SATELLITE_LABELS_LAYER_ID, ASSET_MARKERS_LAYER_ID);
            }
        }
    };
    /**
     * Render the geofence layers
     * @param map the current value of your map ref
     */
    const renderGeofenceLayers = (map: maplibregl.Map) => {
        if (!map.getSource(GEOFENCE_FILL_SOURCE_ID)) {
            map.addSource(GEOFENCE_FILL_SOURCE_ID, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            } as any);
        }
        if (!map.getLayer(GEOFENCE_FILL_LAYER_ID)) {
            map.addLayer({
                id: GEOFENCE_FILL_LAYER_ID,
                type: 'fill',
                source: GEOFENCE_FILL_SOURCE_ID,
                paint: {
                    'fill-color': 'rgba(128,128,128,0.5)',
                    'fill-antialias': true,
                },
                layout: { visibility: 'none' },
            });
        }
        if (!map.getLayer(GEOFENCE_OUTLINE_LAYER_ID)) {
            map.addLayer({
                id: GEOFENCE_OUTLINE_LAYER_ID,
                type: 'line',
                source: GEOFENCE_FILL_SOURCE_ID,
                paint: {
                    'line-color': '#000000',
                    'line-width': 2,
                },
                layout: { visibility: 'none' },
            });
        }
        if (!map.getSource(GEOFENCE_CENTER_SOURCE_ID)) {
            map.addSource(GEOFENCE_CENTER_SOURCE_ID, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            } as any);
        }
        if (!map.getLayer(GEOFENCE_CENTER_LAYER_ID)) {
            map.addLayer({
                id: GEOFENCE_CENTER_LAYER_ID,
                type: 'circle',
                source: GEOFENCE_CENTER_SOURCE_ID,
                minzoom: 0,
                maxzoom: 8,
                paint: {
                    'circle-radius': [
                        'interpolate', ['linear'], ['zoom'],
                        3, 2.5,
                        6, 3.5,
                        9, 5,
                    ],
                    'circle-color': '#000',
                    'circle-stroke-color': '#fff',
                    'circle-stroke-width': 1,
                    'circle-opacity': 0.9,
                },
                layout: {
                    visibility: 'none',
                },
            });
        }
        if (map.getLayer(ASSET_MARKERS_LAYER_ID)) {
            map.moveLayer(GEOFENCE_FILL_LAYER_ID, ASSET_MARKERS_LAYER_ID);
            map.moveLayer(GEOFENCE_OUTLINE_LAYER_ID, ASSET_MARKERS_LAYER_ID);
            map.moveLayer(GEOFENCE_CENTER_LAYER_ID, ASSET_MARKERS_LAYER_ID);
        }
        const geofenceGeojson = createFeatureCollectionFromGeofences(geofences);
        (map.getSource(GEOFENCE_FILL_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)
            ?.setData(geofenceGeojson as any);
        const centersGeojson = createGeofenceCentersFromGeojson(geofenceGeojson as any);
        (map.getSource(GEOFENCE_CENTER_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)
            ?.setData(centersGeojson as any);
        const isVisible = geofenceLayerActive ? 'visible' : 'none';
        map.setLayoutProperty(GEOFENCE_FILL_LAYER_ID, 'visibility', isVisible);
        map.setLayoutProperty(GEOFENCE_OUTLINE_LAYER_ID, 'visibility', isVisible);
        map.setLayoutProperty(GEOFENCE_CENTER_LAYER_ID, 'visibility', isVisible);
    };
    /**
     * Render the layers used for the geofence drawing tool
     * @param map the current value of your map ref
     */
    const renderGeofenceDrawingToolLayers = (map: maplibregl.Map) => {
        const pts = geofenceDraftRef.current;
        if (!map.getSource(GEOFENCE_DRAWING_TOOL_LINE_SOURCE_ID)) {
            map.addSource(GEOFENCE_DRAWING_TOOL_LINE_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
                } as any);
        }
        if (!map.getLayer(GEOFENCE_DRAWING_TOOL_LINE_LAYER_ID)) {
            map.addLayer({
                id: GEOFENCE_DRAWING_TOOL_LINE_LAYER_ID,
                type: 'line',
                source: GEOFENCE_DRAWING_TOOL_LINE_SOURCE_ID,
                paint: {
                    'line-color': '#000',
                    'line-width': 3,
                },
            });
        }
        if (!map.getSource(GEOFENCE_DRAWING_TOOL_FILL_SOURCE_ID)) {
            map.addSource(GEOFENCE_DRAWING_TOOL_FILL_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [], 
                },
            } as any);
        }
        if (!map.getLayer(GEOFENCE_DRAWING_TOOL_FILL_LAYER_ID)) {
            map.addLayer({
                id: GEOFENCE_DRAWING_TOOL_FILL_LAYER_ID,
                type: 'fill',
                source: GEOFENCE_DRAWING_TOOL_FILL_SOURCE_ID,
                paint: {
                    'fill-color': 'rgba(0,0,0,0.15)',
                    'fill-outline-color': '#000',
                },
            });
        }
        if (!map.getSource(GEOFENCE_DRAWING_TOOL_POINTS_SOURCE_ID)) {
            map.addSource(GEOFENCE_DRAWING_TOOL_POINTS_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [] 
                },
            } as any);
        }
        if (!map.getLayer(GEOFENCE_DRAWING_TOOL_POINTS_LAYER_ID)) {
            map.addLayer({
                id: GEOFENCE_DRAWING_TOOL_POINTS_LAYER_ID,
                type: 'circle',
                source: GEOFENCE_DRAWING_TOOL_POINTS_SOURCE_ID,
                paint: {
                    'circle-radius': 4,
                    'circle-color': '#ff6900',
                    'circle-stroke-color': '#000',
                    'circle-stroke-width': 1,
                },
            });
        }
        const lineData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: pts.length ? [{
                type: 'Feature',
                geometry: { 
                    type: 'LineString', 
                    coordinates: pts, 
                },
                properties: {},
            }] : [],
        };
        (map.getSource(GEOFENCE_DRAWING_TOOL_LINE_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(lineData as any);
        const pointsData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: pts.map(([lng, lat]) => ({
                type: 'Feature',
                geometry: { 
                    type: 'Point', 
                    coordinates: [lng, lat], 
                },
                properties: {},
            })),
        };
        (map.getSource(GEOFENCE_DRAWING_TOOL_POINTS_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(pointsData as any);
        const fillData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: (pts.length >= 3) ? [{
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [[...pts, pts[0]]],
                },
                properties: {},
            }] : [],
        };
        (map.getSource(GEOFENCE_DRAWING_TOOL_FILL_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(fillData as any);
    };
    /**
     * Render the layers used for the video search box drawing tool
     * @param map the current value of your map ref
     */
    const renderVideoDrawingToolLayers = (map: maplibregl.Map) => {
        const pts = videoDraftRef.current;
        if (!map.getSource(VIDEO_DRAWING_TOOL_LINE_SOURCE_ID)) {
            map.addSource(VIDEO_DRAWING_TOOL_LINE_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
            } as any);
        }
        if (!map.getLayer(VIDEO_DRAWING_TOOL_LINE_LAYER_ID)) {
            map.addLayer({
                id: VIDEO_DRAWING_TOOL_LINE_LAYER_ID,
                type: 'line',
                source: VIDEO_DRAWING_TOOL_LINE_SOURCE_ID,
                paint: { 
                    'line-color': '#000', 
                    'line-width': 3,
                },
            });
        }
        if (!map.getSource(VIDEO_DRAWING_TOOL_FILL_SOURCE_ID)) {
            map.addSource(VIDEO_DRAWING_TOOL_FILL_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
            } as any);
        }
        if (!map.getLayer(VIDEO_DRAWING_TOOL_FILL_LAYER_ID)) {
            map.addLayer({
                id: VIDEO_DRAWING_TOOL_FILL_LAYER_ID,
                type: 'fill',
                source: VIDEO_DRAWING_TOOL_FILL_SOURCE_ID,
                paint: { 
                    'fill-color': 'rgba(0,0,0,0.15)', 
                    'fill-outline-color': '#000',
                },
            });
        }
        if (!map.getSource(VIDEO_DRAWING_TOOL_POINTS_SOURCE_ID)) {
            map.addSource(VIDEO_DRAWING_TOOL_POINTS_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
            } as any);
        }
        if (!map.getLayer(VIDEO_DRAWING_TOOL_POINTS_LAYER_ID)) {
            map.addLayer({
                id: VIDEO_DRAWING_TOOL_POINTS_LAYER_ID,
                type: 'circle',
                source: VIDEO_DRAWING_TOOL_POINTS_SOURCE_ID,
                paint: {
                    'circle-radius': 4,
                    'circle-color': '#ff6900',
                    'circle-stroke-color': '#000',
                    'circle-stroke-width': 1,
                },
            });
        }
        const lineData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: pts.length
                ? [{ type: 'Feature', geometry: { type: 'LineString', coordinates: pts }, properties: {} }]
                : [],
        };
        (map.getSource(VIDEO_DRAWING_TOOL_LINE_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(lineData as any);
        const pointsData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: pts.map(([lng, lat]) => ({
                type: 'Feature',
                geometry: { 
                    type: 'Point', 
                    coordinates: [lng, lat],
                },
                properties: {},
            })),
        };
        (map.getSource(VIDEO_DRAWING_TOOL_POINTS_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(pointsData as any);
        const fillData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features:
            pts.length >= 3
                ? [{ 
                    type: 'Feature', 
                    geometry: { 
                        type: 'Polygon', 
                        coordinates: [[...pts, pts[0]]], 
                    }, 
                    properties: {},
                }]
                : [],
        };
        (map.getSource(VIDEO_DRAWING_TOOL_FILL_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(fillData as any);
    };
    /**
     * Render the heatmap visual layers
     * @param map the current value of your map ref
     */
    const renderHeatmapLayers = (map: maplibregl.Map) => {
        if (!map.getSource(HEATMAP_SOURCE_ID)) {
            map.addSource(HEATMAP_SOURCE_ID, {
                type: 'geojson',
                data: { 
                    type: 'FeatureCollection', 
                    features: [],
                },
            } as any);
        }
        loadSnailTrailEventIconImage(map, SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID);
        if (!map.getLayer(HEATMAP_LAYER_ID)) {
            map.addLayer({
                id: HEATMAP_LAYER_ID,
                type: 'heatmap',
                source: HEATMAP_SOURCE_ID,
                maxzoom: 22,
                paint: {
                    'heatmap-weight': ['interpolate', ['exponential', 0.5], ['get', 'w'], 0, 0, 1, 1],
                    'heatmap-intensity': ['interpolate', ['linear'], ['zoom'],
                        0, 1.2,
                        9, 2.2,
                        13, 3.2,
                    ],
                    'heatmap-radius': ['interpolate', ['linear'], ['zoom'],
                        5, 24,
                        9, 48,
                        13, 84,
                        16, 120,
                        20, 160,
                    ],
                    'heatmap-color': [
                    'interpolate', ['linear'], ['heatmap-density'],
                        0.00, 'rgba(0,0,0,0)',
                        0.15, 'rgba(0,170,0,0.55)',
                        0.35, 'rgba(180,220,0,0.75)',
                        0.55, 'rgba(255,210,0,0.90)',
                        0.75, 'rgba(255,140,0,0.95)',
                        1.00, 'rgba(255,0,0,1.00)',
                    ],
                    'heatmap-opacity': ['interpolate', ['linear'], ['zoom'],
                        10, 0.85,
                        16, 0.40,
                        18, 0.30,
                    ],
                },
                layout: { visibility: 'visible' },
            });
        }
        if (!map.getLayer(HEATMAP_EVENTS_ICON_LAYER_ID) && map.hasImage(SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID)) {
            map.addLayer({
                id: HEATMAP_EVENTS_ICON_LAYER_ID,
                type: 'symbol',
                source: HEATMAP_SOURCE_ID,
                layout: {
                    'icon-image': SNAIL_TRAIL_EVENTS_ICON_IMAGE_ID,
                    'icon-size': 1.0,
                    'icon-rotation-alignment': 'viewport',
                    'icon-allow-overlap': true,
                    'text-allow-overlap': true,
                    'symbol-z-order': 'auto',
                    'symbol-sort-key': 0,
                },
                paint: {},
            });
        }
        const anchor = map.getLayer(ASSET_MARKERS_LAYER_ID) 
            ? ASSET_MARKERS_LAYER_ID 
            : undefined;
        if (map.getLayer(HEATMAP_EVENTS_ICON_LAYER_ID)) {
            map.moveLayer(HEATMAP_EVENTS_ICON_LAYER_ID, anchor);
        }
        if (map.getLayer(HEATMAP_LAYER_ID)) {
            map.moveLayer(HEATMAP_LAYER_ID, anchor);
        }
        (map.getSource(HEATMAP_SOURCE_ID) as maplibregl.GeoJSONSource)?.setData(eventTypesHeatmapGeojson as any);
        if (!(map as any).__heatmapEventHoverBound) {
            const state = { raf: 0 as number | 0 };
            let openTimer: number | 0 = 0 as number | 0;
            let closeTimer: number | 0 = 0 as number | 0;
            let popupMouseOver = false;
            let popupRoot: ReturnType<typeof createRoot> | null = null;
            const clearCloseTimer = () => { 
                if (closeTimer) { 
                    window.clearTimeout(closeTimer as any); 
                    closeTimer = 0 as number | 0; 
                } 
            };
            const clearOpenTimer = () => { 
                if (openTimer) { 
                    window.clearTimeout(openTimer as any); 
                    openTimer = 0 as number | 0; 
                } 
            };
            const scheduleClose = () => {
                clearCloseTimer();
                if (!eventMarkerPopupRef.current) {
                    return;
                }
                const popup = eventMarkerPopupRef.current;
                closeTimer = window.setTimeout(() => {
                    if (!popupMouseOver && eventMarkerPopupRef.current === popup) {
                        try { 
                            popup.remove(); 
                        } catch {}
                        popupRoot?.unmount();
                        popupRoot = null;
                        eventMarkerPopupRef.current = null;
                    }
                }, 300) as unknown as number;
            };
            const attachPopupGuards = (popup: maplibregl.Popup) => {
                const el = popup.getElement?.();
                if (!el) {
                    return requestAnimationFrame(() => attachPopupGuards(popup));
                }
                el.style.zIndex = String(21 + 2);
                el.addEventListener('mouseenter', () => { 
                    popupMouseOver = true; clearCloseTimer(); 
                });
                el.addEventListener('mouseleave', () => { 
                    popupMouseOver = false; scheduleClose(); 
                });
            };
            const openPopup = (coords: [number, number], props: any) => {
                clearOpenTimer();
                clearCloseTimer();
                if (eventMarkerPopupRef.current) {
                    try { 
                        eventMarkerPopupRef.current.remove(); 
                    } catch {}
                    eventMarkerPopupRef.current = null;
                }
                const container = document.createElement('div');
                popupRoot?.unmount();
                popupRoot = createRoot(container);
                popupRoot.render(
                    <Provider store={store}>
                        <AssetPopup
                            assetId={props.asset_id}
                            date={selectedDate.format('YYYY-MM-DD')}
                            uomPref={uomPref}
                            hideQuickButtons
                            sendEventRequest
                            recordId={props.record_id}
                        />
                    </Provider>
                );
                const popup = new maplibregl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    anchor: 'bottom',
                    offset: [0, -16] as maplibregl.PointLike,
                    className: 'event-popup-base',
                    maxWidth: 'auto',
                })
                    .setLngLat(coords)
                    .setDOMContent(container)
                    .addTo(map);
                eventMarkerPopupRef.current = popup;
                attachPopupGuards(popup);
                popup.on('close', () => {
                    clearCloseTimer();
                    popupRoot?.unmount();
                    popupRoot = null;
                    if (eventMarkerPopupRef.current === popup) {
                        eventMarkerPopupRef.current = null;
                    }
                    popupMouseOver = false;
                });
            };
            const onEnter = () => { 
                map.getCanvas().style.cursor = 'pointer'; 
                clearCloseTimer(); 
            };
            const onLeave = () => { 
                map.getCanvas().style.cursor = (geofenceDrawingToolActiveRef.current || videoDrawingToolActiveRef.current ? PENCIL_CURSOR_SVG : ''); 
                clearOpenTimer(); 
                scheduleClose(); 
            };
            const onMove = (e: maplibregl.MapLayerMouseEvent) => {
                if (state.raf) {
                    return;
                }
                state.raf = requestAnimationFrame(() => {
                    state.raf = 0;
                    const f = map.queryRenderedFeatures(e.point, { layers: [HEATMAP_EVENTS_ICON_LAYER_ID] })?.[0];
                    if (!f) {
                        clearOpenTimer();
                        scheduleClose();
                        map.getCanvas().style.cursor = (geofenceDrawingToolActiveRef.current || videoDrawingToolActiveRef.current ? PENCIL_CURSOR_SVG : '');
                        return;
                    }
                    clearCloseTimer();
                    map.getCanvas().style.cursor = 'pointer';
                    const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
                    const props = f.properties || {};
                    if (!eventMarkerPopupRef.current) {
                        clearOpenTimer();
                        openTimer = window.setTimeout(() => {
                            openPopup(coords, props);
                            openTimer = 0 as number | 0;
                        }, 200) as unknown as number;
                    } else {
                        try { 
                            eventMarkerPopupRef.current.setLngLat(coords); 
                        } catch {}
                    }
                });
            };
            map.on('mouseenter', HEATMAP_EVENTS_ICON_LAYER_ID, onEnter);
            map.on('mouseleave', HEATMAP_EVENTS_ICON_LAYER_ID, onLeave);
            map.on('mousemove', HEATMAP_EVENTS_ICON_LAYER_ID, onMove);
            (map as any).__heatmapEventHoverBound = { 
                onEnter, 
                onLeave, 
                onMove, 
            };
        }
    };
    /**
     * Set the current hovered asset (bring to front & highlight), does nothing if asset id is not shown as a marker on the map
     * @param map the current value of your map ref
     * @param assetId the asset id to focus on
     */
    const setHoveredAsset = (
        map: maplibregl.Map,
        asset: number | maplibregl.MapGeoJSONFeature | null,
    ) => {
        if (!map) {
            return;
        }
        if (!map.getLayer(ASSET_MARKER_HOVER_LAYER_ID)) {
            return;
        }
        if (asset == null) {
            hoveredAssetIdRef.current = null;
            if (map.getLayer(ASSET_MARKER_HOVER_LAYER_ID)) {
                map.setFilter(ASSET_MARKER_HOVER_LAYER_ID, ['==', ['to-number', ['get','asset_id']], -1]);
                map.setFilter(ASSET_HOVER_RING_LAYER_ID, ['==', ['to-number', ['get', 'asset_id']], -1]);
            }
            return;
        }
        let feat: any | undefined;
        if (typeof asset !== 'number') {
            feat = asset;
            hoveredAssetIdRef.current = Number((feat.properties as any)?.asset_id);
        } else {
            const id = asset;
            hoveredAssetIdRef.current = id;
            const geojsonSrc = markersGeojsonRef.current;
            feat = (geojsonSrc.features as any[]).find(f => Number(f?.properties?.asset_id) === id);
            if (!feat) {
                if (map.getLayer(ASSET_MARKER_HOVER_LAYER_ID)) {
                    map.setFilter(ASSET_MARKER_HOVER_LAYER_ID, ['==', ['to-number', ['get','asset_id']], -1]);
                }
                return;
            }
        }
        const p: any = feat.properties || {};
        if (p.division_color && p.arrow_color) {
            const angle = Number(p.angle) || 0;
            loadAssetMarker(map, p.division_color, p.arrow_color, angle, false);
            loadAssetMarker(map, p.division_color, p.arrow_color, angle, true);
        }
        if (map.getLayer(ASSET_MARKER_HOVER_LAYER_ID)) {
            map.setFilter(ASSET_MARKER_HOVER_LAYER_ID, ['==', ['to-number', ['get','asset_id']], hoveredAssetIdRef.current]);
            map.setFilter(ASSET_HOVER_RING_LAYER_ID, ['==', ['to-number', ['get', 'asset_id']], hoveredAssetIdRef.current]);
            map.moveLayer(ASSET_HOVER_RING_LAYER_ID);
        }
    };
    /**
     * Zoom to any given array of asset ids. If none are given then defaults to all checked assets
     * @param assetIds an array of asset ids
     */
    const zoomTo = (assetIds?: number[] | null) => {
        if (mapAlreadyInMotionRef.current) {
            mapAlreadyInMotionRef.current = false;
            return;
        }
        if (zoomToRequestedAnimationFrameRef.current) {
            cancelAnimationFrame(zoomToRequestedAnimationFrameRef.current);
        }
        zoomToRequestedAnimationFrameRef.current = requestAnimationFrame(() => {
            performZoom(assetIds);
            zoomToRequestedAnimationFrameRef.current = null;
        });
        const performZoom = (assetIds?: number[] | null) => {
            const map = mapRef.current;
            if (!map) {
                return;
            }
            const idSet = assetIds && assetIds.length ? new Set(assetIds) : null;
            const features = markersGeojson.features as Feature<Point, GeoJsonProperties>[];
            const pts: [number, number][] = [];
            for (const f of features) {
                const id = (f.properties as any)?.asset_id as number | undefined;
                if (idSet && (id == null || !idSet.has(id))) {
                    continue;
                }
                const coords = f.geometry?.coordinates;
                if (Array.isArray(coords) && coords.length >= 2) {
                    const lng = coords[0];
                    const lat = coords[1];
                    if (isValidLngLat(lng, lat)) {
                        pts.push([lng as number, lat as number]);
                    }
                }
            }
            if (pts.length === 0) {
                return;
            }
            if (pts.length === 1) {
                const [lng, lat] = pts[0];
                map.stop();
                map.easeTo({
                    center: [lng, lat],
                    zoom: EASE_TO_DEFAULT_ZOOM,
                    duration: 0,
                    essential: true,
                });
                return;
            }
            let minLng = Infinity;
            let minLat = Infinity;
            let maxLng = -Infinity;
            let maxLat = -Infinity;
            for (const [lng, lat] of pts) {
                if (lng < minLng) {
                    minLng = lng;
                }
                if (lat < minLat) {
                    minLat = lat;
                }
                if (lng > maxLng) {
                    maxLng = lng;
                }
                if (lat > maxLat) {
                    maxLat = lat;
                }
            }
            map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
                padding: MAP_CAMERA_DEFAULT_PADDING,
                maxZoom: FIT_BOUNDS_MAX_ZOOM,
                duration: FIT_BOUNDS_DEFAULT_DURATION,
            });
        }
    }
    /**
     * Similar to zoomTo() except instant and uses an unfiltered assetMarkers list (checked and unchecked assets).
     * Helps to avoid race conditions when assetMarker updates are not yet re-calculated but we need to zoom
     * @param assetIds an array of asset ids
     */
    const jumpTo = (assetIds?: number[] | null) => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        const coordsById = assetMarkersLngLatByIdRef.current;
        const targets: [number, number][] = [];
        if (assetIds && assetIds.length) {
            const uniqueIds = new Set<number>(
                assetIds.filter((v) => Number.isFinite(v)) as number[],
            );
            uniqueIds.forEach((id) => {
                let coords: [number, number] | null = null;
                if (mapMode === MapMode.Historic) {
                    if (
                        selectedSnailTrailAssetId &&
                        id === selectedSnailTrailAssetId &&
                        selectedSnailTrail &&
                        selectedSnailTrail.length
                    ) {
                        const secondsSinceMidnight = sliderValue * sliderStepInSeconds;
                        const sliderMoment = selectedDate
                            .startOf('day')
                            .add(secondsSinceMidnight, 'seconds');
                        let bestPoint: SnailTrailPoint | null = null;
                        let bestDeltaSeconds = Infinity;
                        for (const pt of selectedSnailTrail) {
                            const ptTime = dayjs(pt.time);
                            const delta = Math.abs(ptTime.diff(sliderMoment, 'seconds'));
                            if (delta < bestDeltaSeconds) {
                                bestDeltaSeconds = delta;
                                bestPoint = pt;
                            }
                        }
                        if (bestPoint) {
                            const trailCoords = getLngLatFromSnailTrailPoint(bestPoint);
                            if (Array.isArray(trailCoords) && isValidLngLat(trailCoords[0] as number, trailCoords[1] as number)) {
                                coords = [trailCoords[0] as number, trailCoords[1] as number];
                            }
                        }
                    }
                    if (!coords && markersGeojson && markersGeojson.features?.length) {
                        const f = (markersGeojson.features as any[]).find((ff) => Number(ff?.properties?.asset_id) === id);
                        const featureCoords =(f?.geometry as GeoJSON.Point)?.coordinates as | [number, number] | undefined;
                        if (featureCoords && isValidLngLat(featureCoords[0], featureCoords[1])) {
                            coords = featureCoords;
                        }
                    }
                }
                if (!coords) {
                    const liveCoords = coordsById.get(id);
                    if (liveCoords && isValidLngLat(liveCoords[0], liveCoords[1])) {
                        coords = liveCoords;
                    }
                }
                if (coords) {
                    targets.push(coords);
                }
            });
        } else {
            coordsById.forEach((c) => {
                if (c && isValidLngLat(c[0], c[1])) {
                    targets.push(c);
                }
            });
        }
        if (!targets.length) {
            return;
        }
        if (targets.length === 1) {
            map.jumpTo({
                center: targets[0],
                zoom: JUMP_TO_DEFAULT_ZOOM,
            });
            return;
        }
        const bounds = new maplibregl.LngLatBounds(targets[0], targets[0]);
        for (let i = 1; i < targets.length; i++) {
            bounds.extend(targets[i]);
        }
        const cameraFromBounds = map.cameraForBounds(bounds, {
            padding: MAP_CAMERA_DEFAULT_PADDING,
            maxZoom: FIT_BOUNDS_MAX_ZOOM,
        });
        if (cameraFromBounds) {
            map.jumpTo(cameraFromBounds);
        }
    };
    /**
     * Toggle between live and historic map modes, also selects/deselects all assets when switching to live/historic mode respectively
     */
    const toggleMapMode = () => {
        const newMapMode = mapMode === MapMode.Live ? MapMode.Historic : MapMode.Live
        performChangeMapMode(newMapMode);
    }
    /**
     * Sets the map mode and also selects/deselects all assets when switching to live/historic mode respectively.
     * Allows more control than toggleMapMode().
     * If already in the provided mode then does nothing.
     * @param newMapMode the new map mode to set
     */
    const setMapMode = (
        newMapMode: MapMode, 
        newCheckedAssets: string[] | null = null,
    ) => {
        if (newMapMode === mapMode) {
            return;
        }
        performChangeMapMode(newMapMode, newCheckedAssets);
    }
    /**
     * Performs the actual map mode change, usually used indirectly
     * @param newMapMode the new map mode to set
     * @param newCheckedAssets optional new checked assets to set as part of mode change
     */
    const performChangeMapMode = (
        newMapMode: MapMode,
        newCheckedAssets: string[] | null = null,
    ) => {
        if (onChangeMapMode) {
            onChangeMapMode(newMapMode);
        }
        if (newMapMode === MapMode.Live) {
            dispatch(setSelectedDate(dayjs()));
            dispatch(setCheckedAssets(newCheckedAssets || [SELECT_ALL_VALUE]));
            dispatch(resetSnailTrail());
            setSelectedSnailTrailAssetId(null);
        } else if (newMapMode === MapMode.Historic) {
            dispatch(setCheckedAssets(newCheckedAssets || []));
        }
    };
    /**
     * Get the props needed to map out the render for the toolbar buttons
     * @param map the current value of your map ref
     */
    const getToolbarButtonProps = (map: maplibregl.Map): MapToolbarButtonProps[] => {
        const ACTIVE_COLOR = "var(--safety-orange)";
        const INACTIVE_COLOR = "var(--cool-gray-60)";
        return [{
            key: MapToolbarButtonType.ToggleAssetMarkerNames,
            icon: (
                <Information
                    size="16px"
                    color={assetMarkerNamesActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: assetMarkerNamesActive ? 'Hide Asset Names' : 'Show Asset Names',
            onClick: () => { 
                const newActive = !assetMarkerNamesActive;
                setAssetMarkerNamesActive(newActive); 
            },
        },
        {
            key: MapToolbarButtonType.ToggleTrafficOverlay,
            icon: (
                <TrafficEvent
                    size="16px"
                    color={trafficLayerActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: trafficLayerActive ? 'Hide Traffic Overlay' : 'Show Traffic Overlay',
            onClick: () => { 
                const newActive = !trafficLayerActive;
                setTrafficLayerActive(newActive); 
                setTrafficOverlayLayerVisibility(map, newActive);
            },
        },
        {
            key: MapToolbarButtonType.GeofencesMenu,
            icon: (
                <EarthEuropeAfrica
                    size="16px"
                    color={(geofenceLayerActive || geofenceDrawingToolActive) ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: 'Geo-fences',
            menuOptions: [{
                key: 'toggle-geofences',
                label: geofenceLayerActive ? 'Hide Geo-fences' : 'Show Geo-fences',
            },
            {
                key: 'draw-geofence',
                label: geofenceDrawingToolActive ? 'Cancel Drawing' : 'Draw Geo-fence',
            },
        ]},
        {
            key: MapToolbarButtonType.ToggleLocationSearch,
            icon: (
                <LocationIcon
                    size="16px"
                    color={locationSearchActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: locationSearchActive ? 'Hide Location Search' : 'Show Location Search',
            onClick: () => { 
                const newActive = !locationSearchActive;
                setLocationSearchActive(newActive); 
            },
        },
        {
            key: MapToolbarButtonType.ToggleSatelliteView,
            icon: (
                <Satellite
                    size="16px"
                    color={satelliteModeActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: satelliteModeActive ? 'Hide Satellite View' : 'Show Satellite View',
            onClick: () => { 
                const newActive = !satelliteModeActive;
                setSatelliteModeActive(newActive); 
            },
        },
        {
            key: MapToolbarButtonType.HeatmapsMenu,
            icon: (
                <HeatMap_02
                    size="16px"
                    color={eventTypesHeatmapGeojson?.features?.length ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: 'Heatmaps',
        },
        {
            key: MapToolbarButtonType.ToggleVideoSearch,
            icon: (
                <Video
                    size="16px"
                    color={videoDrawingToolActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                />
            ),
            tooltip: videoDrawingToolActive ? 'Hide Video Search' : 'Show Video Search',
            onClick: () => {
                const map = mapRef.current;
                if (!map) {
                    return;
                }
                if (!videoDrawingToolActive) {
                    if (geofenceDrawingToolActive) {
                        cancelGeofenceDrawingAndCleanup(map);
                    }
                    beginVideoDrawing(map);
                } else {
                    cancelVideoDrawingAndCleanup(map);
                }
            },
        }];
    }
    /**
     * Handle toolbar menu clicks (nested)
     * @param btnKey the key of the btn that was clicked e.g. the base hitzone to open/close the menu
     * @param optionKey the key of the menu option that was clicked e.g. an entry in the menu
     */
    const handleToolbarMenuClick = (
        btnKey: MapToolbarButtonType,
        optionKey: string
    ) => {
        switch (btnKey) {
            case MapToolbarButtonType.GeofencesMenu:
                if (optionKey === 'toggle-geofences') {
                    const newGeofenceVisibility = !geofenceLayerActive;
                    setGeofenceLayerActive(newGeofenceVisibility);
                    if (newGeofenceVisibility) {
                        shouldZoomToGeofencesRef.current = true;
                        dispatch(fetchGeofencesRequest());
                    } else {
                        shouldZoomToGeofencesRef.current = false;
                        zoomTo();
                    }
                } else if (optionKey === 'draw-geofence') {
                    const map = mapRef.current;
                    if (map && !geofenceDrawingToolActive) {
                        if (videoDrawingToolActive) {
                            cancelVideoDrawingAndCleanup(map);
                        }
                        beginGeofenceDrawing(map);
                    } else {
                        cancelGeofenceDrawingAndCleanup(map);
                    }
                }
                break;
            case MapToolbarButtonType.HeatmapsMenu:
                // handled in the oncheck handler for antd tree we render for heatmaps
                break;
            default:
                break;
        }
    };
    /**
     * Set visibility of the traffic overlay layer
     * @param map the current value of your map ref
     * @param visible the boolean value to set
     */
    const setTrafficOverlayLayerVisibility = (
        map: maplibregl.Map, 
        visible: boolean,
    ) => {
        renderTrafficLayer(map);
        map.setLayoutProperty(TRAFFIC_LAYER_ID, 'visibility', visible ? 'visible' : 'none');
    };
    /**
     * Set visibility of the satellite labels overlay layer
     * @param map the current value of your map ref
     * @param visible the boolean value to set
     */
    const setSatelliteLabelsOverlayVisibility = (
        map: maplibregl.Map, 
        visible: boolean,
    ) => {
        if (map.getLayer(SATELLITE_LABELS_LAYER_ID)) {
            map.setLayoutProperty(SATELLITE_LABELS_LAYER_ID, 'visibility', visible ? 'visible' : 'none');
        }
    }
    /**
     * Start geofence drawing, add layers
     * @param map the current value of your map ref
     */
    const beginGeofenceDrawing = (map: maplibregl.Map) => {
        geofenceDraftRef.current = [];
        setGeofenceDrawingToolActive(true);
        renderGeofenceDrawingToolLayers(map);
        const clickHandler = handleGeofenceDrawingToolClick(map);
        map.on('click', clickHandler);
        geofenceDrawingToolHandlersRef.current = { click: clickHandler };
    };
    /**
     * Handle click when in geofence drawing tool mode
     * @param map the current value of your map ref
     */
    const handleGeofenceDrawingToolClick = (map: maplibregl.Map) => (e: maplibregl.MapMouseEvent) => {
        const { lng, lat } = e.lngLat;
        const pts = geofenceDraftRef.current;
        if (pts.length >= 3) {
            const first = pts[0];
            const a = map.project({ 
                lng: first[0], lat: 
                first[1],
            });
            const b = map.project({
                lng, 
                lat, 
            });
            const distPx = Math.hypot(a.x - b.x, a.y - b.y);
            if (distPx <= GEOFENCE_DRAWING_TOOL_MIN_DISTANCE_TO_CLOSE) {
                completeGeofenceDrawing(map);
                return;
            }
        }
        pts.push([lng, lat]);
        geofenceDraftRef.current = pts;
        renderGeofenceDrawingToolLayers(map);
    };
    /**
     * Cancel ongoing geofence drawing, handler and hide layers
     * @param map the current value of your map ref
     */
    const cancelGeofenceDrawingAndCleanup = (map?: maplibregl.Map | null) => {
        const m = map || mapRef.current;
        if (!m) {
            return;
        }
        const handler = geofenceDrawingToolHandlersRef.current;
        if (handler?.click) {
            m.off('click', handler.click);
        }
        geofenceDrawingToolHandlersRef.current = null;
        removeGeofenceDrawingToolLayers(m);
        geofenceDraftRef.current = [];
        setGeofenceDrawingToolActive(false);
    };
    /**
     * Finish geofence drawing and pipe results to geofences view
     * @param map the current value of your map ref
     */
    const completeGeofenceDrawing = (map: maplibregl.Map) => {
        const pts = geofenceDraftRef.current;
        if (pts.length < 3) {
            cancelGeofenceDrawingAndCleanup(map);
            notification.error({
                message: 'Error',
                description: 'Geofences must have at least 3 points',
            })
            return;
        }
        cancelGeofenceDrawingAndCleanup(map);
        const mappedPoints = pts.map(p => { 
            return {
                lng: p[0], 
                lat: p[1], 
            };
        });
        mappedPoints.push(mappedPoints[0]);
        dispatch(setDataEventToReportParams({
            geoPoints: mappedPoints,
            dateRange: [
                dayjs(selectedDate).startOf('day'), 
                dayjs(selectedDate).endOf('day'),
            ],
        }));
        history.push('/reports/geofence-report');
    };
    /**
     * Start video search box drawing, add layers
     * @param map the current value of your map ref
     */
    const beginVideoDrawing = (map: maplibregl.Map) => {
        if (geofenceDrawingToolActive) {
            cancelGeofenceDrawingAndCleanup(map);
        }
        videoDraftRef.current = [];
        setVideoDrawingToolActive(true);
        renderVideoDrawingToolLayers(map);
        const clickHandler = handleVideoDrawingToolClick(map);
        map.on('click', clickHandler);
        videoDrawingToolHandlersRef.current = { click: clickHandler };
    };
    /**
     * Handle click when in video search box drawing tool mode
     * @param map the current value of your map ref
     */
    const handleVideoDrawingToolClick = (map: maplibregl.Map) => (e: maplibregl.MapMouseEvent) => {
        const { lng, lat } = e.lngLat;
        const pts = videoDraftRef.current;
        if (pts.length >= 3) {
            const [flng, flat] = pts[0];
            const a = map.project({ 
                lng: flng, 
                lat: flat,
            });
            const b = map.project({ lng, lat });
            const distPx = Math.hypot(a.x - b.x, a.y - b.y);
            if (distPx <= GEOFENCE_DRAWING_TOOL_MIN_DISTANCE_TO_CLOSE) {
                completeVideoDrawing(map);
                return;
            }
        }
        pts.push([lng, lat]);
        videoDraftRef.current = pts;
        renderVideoDrawingToolLayers(map);
    };
    /**
     * Cancel ongoing video search box drawing, handler and hide layers
     * @param map the current value of your map ref
     */
    const cancelVideoDrawingAndCleanup = (map?: maplibregl.Map | null) => {
        const m = map || mapRef.current;
        if (!m) {
            return;
        }
        const handler = videoDrawingToolHandlersRef.current;
        if (handler?.click) {
            m.off('click', handler.click);
        }
        videoDrawingToolHandlersRef.current = null;
        removeVideoDrawingToolLayers(m);
        videoDraftRef.current = [];
        setVideoDrawingToolActive(false);
    };
    /**
     * Finish video serach box drawing and pipe results to video search view
     * @param map the current value of your map ref
     */
    const completeVideoDrawing = (map: maplibregl.Map) => {
        const pts = videoDraftRef.current;
        if (pts.length < 3) {
            cancelVideoDrawingAndCleanup(map);
            notification.error({ 
                message: 'Error', 
                description: 'Video search area must have at least 3 points', 
            });
            return;
        }
        const polygon = pts.map(([lng, lat]) => ({ lng, lat }));
        polygon.push(polygon[0]);
        cancelVideoDrawingAndCleanup(map);
        dispatch(setMapSearchLocationBoundary({
            polygon,
            devices: checkedAssetsForCompare,
            selectedDate: selectedDate.format('YYYY-MM-DD'),
            mapSliderValue: sliderValue,
            mapMode,
            selectedSnailTrailAssetId,
        }));
        history.push('/video/video-search/location');
    };
    /**
     * Convert raw slider value to user-friendly tooltip
     * @param value the slider value directly provided by the slider
     */
    const formatTooltip = (value?: number) => {
        if (value === undefined) {
            return (
                <div />
            );
        }
        let valueAsTime: string;
        let secondsSinceMidnight: number;
        if (selectedSnailTrailAssetId) {
            secondsSinceMidnight = value * 10;
            valueAsTime = selectedDate.startOf("day").add(secondsSinceMidnight, "seconds").format("HH:mm:ss");
        } else {
            secondsSinceMidnight = value * 10 * 60;
            valueAsTime = selectedDate.startOf("day").add(secondsSinceMidnight, "seconds").format("HH:mm");
        }
        let status = '';
        let journeyName = '';
        let journeyTimeRange = '';
        let match: DeviceTimelineSegment | null = null;
        if (timelineData?.length) {
            for (let i = 0; i < timelineData.length; i++) {
                if (secondsSinceMidnight > timelineData[i].start && secondsSinceMidnight <= timelineData[i].end) {
                    match = timelineData[i];
                }
            }
        }
        if (match) {
            status = getDeviceTimelineStatusText(match.status);
            journeyName = match.journey_name;
            journeyTimeRange = match.journey_start && match.journey_end 
                ? `${dayjs(match.journey_start).format('HH:mm:ss')} - ${dayjs(match.journey_end).format('HH:mm:ss')}` 
                : '';
        }
        if (checkedAssets?.length) {
            return (
                <div style={{ textAlign: 'center' }}>
                    {journeyName ? (
                        <div>{journeyName}</div>
                    ) : null}
                    {journeyTimeRange ? (
                        <div>{journeyTimeRange}</div>
                    ) : null}
                    {status ? (
                        <div>{status}</div>
                    ) : null}
                    <div>{valueAsTime}</div>
                </div>
            );
        } else {
            return (
                <div style={{ textAlign: 'center' }}>
                    No Assets Selected
                    <div>{valueAsTime}</div>
                </div>
            )
        }
        
    };
    /**
     * Remove snail trail layers (GPU)
     * @param map the current value of your map ref
     */
    const removeSnailTrailLayers = (map: maplibregl.Map): void => {
        if (!map) {
            return;
        }
        try {
            safelyRemoveMapLayer(map, SNAIL_TRAIL_START_END_LABEL_LAYER_ID);
            safelyRemoveMapLayer(map, SNAIL_TRAIL_START_END_CIRCLE_LAYER_ID);
            safelyRemoveMapLayer(map, SNAIL_TRAIL_DIRECTIONAL_ARROWS_LAYER_ID);
            safelyRemoveMapLayer(map, SNAIL_TRAIL_LINE_LAYER_ID);
            safelyRemoveMapSource(map, SNAIL_TRAIL_START_END_SOURCE_ID);
            safelyRemoveMapSource(map, SNAIL_TRAIL_LINE_SOURCE_ID);
            safelyRemoveMapSource(map, SNAIL_TRAIL_EVENTS_SOURCE_ID);
            safelyRemoveMapImage(map, SNAIL_TRAIL_DIRECTIONAL_ARROWS_IMAGE_ID);
            const snailTrailDirectionalArrowZoomHandler = (map as any).__snailTrailDirectionalArrowZoomHandler;
            if (snailTrailDirectionalArrowZoomHandler) {
                map.off('zoom', snailTrailDirectionalArrowZoomHandler);
                (map as any).__snailTrailDirectionalArrowZoomHandler = null;
            }
        } catch {}
    };
    /**
     * Remove traffic overlay layer
     * @param map the current value of your map ref
     */
    const removeTrafficLayer = (map: maplibregl.Map): void => {
        try {
            safelyRemoveMapLayer(map, TRAFFIC_LAYER_ID);
            safelyRemoveMapSource(map, TRAFFIC_SOURCE_ID);
        } catch {}
    };
    /**
     * Remove the geofence layers
     * @param map the current value of your map ref
     */
    const removeGeofenceLayers = (map: maplibregl.Map): void => {
        try {
            safelyRemoveMapLayer(map, GEOFENCE_CENTER_LAYER_ID);
            safelyRemoveMapLayer(map, GEOFENCE_OUTLINE_LAYER_ID);
            safelyRemoveMapLayer(map, GEOFENCE_FILL_LAYER_ID);

            safelyRemoveMapSource(map, GEOFENCE_CENTER_SOURCE_ID);
            safelyRemoveMapSource(map, GEOFENCE_FILL_SOURCE_ID);
        } catch {}
    };
    /**
     * Remove the layers for the geofence drawing tool
     * @param map the current value of your map ref
     */
    const removeGeofenceDrawingToolLayers = (map: maplibregl.Map): void => {
        safelyRemoveMapLayer(map, GEOFENCE_DRAWING_TOOL_POINTS_LAYER_ID);
        safelyRemoveMapLayer(map, GEOFENCE_DRAWING_TOOL_LINE_LAYER_ID);
        safelyRemoveMapLayer(map, GEOFENCE_DRAWING_TOOL_FILL_LAYER_ID);
        safelyRemoveMapSource(map, GEOFENCE_DRAWING_TOOL_POINTS_SOURCE_ID);
        safelyRemoveMapSource(map, GEOFENCE_DRAWING_TOOL_LINE_SOURCE_ID);
        safelyRemoveMapSource(map, GEOFENCE_DRAWING_TOOL_FILL_SOURCE_ID);
    };
    /**
     * Remove the layers for the video search box drawing tool
     * @param map the current value of your map ref
     */
    const removeVideoDrawingToolLayers = (map: maplibregl.Map): void => {
        safelyRemoveMapLayer(map, VIDEO_DRAWING_TOOL_POINTS_LAYER_ID);
        safelyRemoveMapLayer(map, VIDEO_DRAWING_TOOL_LINE_LAYER_ID);
        safelyRemoveMapLayer(map, VIDEO_DRAWING_TOOL_FILL_LAYER_ID);
        safelyRemoveMapSource(map, VIDEO_DRAWING_TOOL_POINTS_SOURCE_ID);
        safelyRemoveMapSource(map, VIDEO_DRAWING_TOOL_LINE_SOURCE_ID);
        safelyRemoveMapSource(map, VIDEO_DRAWING_TOOL_FILL_SOURCE_ID);
    };
    /**
     * Remove the layers for the heatmap
     * @param map 
     */
    const removeHeatmapLayers = (map: maplibregl.Map): void => {
        try {
            safelyRemoveMapLayer(map, HEATMAP_LAYER_ID);
            safelyRemoveMapSource(map, HEATMAP_SOURCE_ID);
        } catch {}
    };

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        dispatch(setFocusAsset(null));
        dispatch(setDeviceHeatMap(null));
        dispatch(getEventTypesRequest());
        maplibregl.addProtocol('google', googleProtocol);
        const map = new maplibregl.Map({
            container: containerRef.current,
            style: (() => { return getMapStyle(satelliteModeActive); })(),
            center: constants.DEFAULT_MAP_CENTER_MAP_LIBRE,
            zoom: constants.DEFAULT_MAP_ZOOM_MAP_LIBRE,
        });
        mapRef.current = map;
        const onStart = () => { 
            mapIsMovingRef.current = true; 
        };
        const onIdle = () => { 
            mapIsMovingRef.current = false; 
        };
        map.on('movestart', onStart);
        map.on('idle', onIdle);
        map.on('load', () => {
            renderMapFeatures(map);
        });
        const start = () => { 
            userIsInteractingRef.current = true; 
        };
        const stop = () => { 
            userIsInteractingRef.current = false; 
        };
        map.on('dragstart', start);
        map.on('zoomstart', start);
        map.on('rotatestart', start);
        map.on('pitchstart', start);
        map.on('idle', stop);
        return () => {
            if (assetMarkerPopupRef.current) {
                assetMarkerPopupRef.current.remove();
                assetMarkerPopupRef.current = null;
            }
            if (eventMarkerPopupRef.current) {
                eventMarkerPopupRef.current.remove();
                eventMarkerPopupRef.current = null;
            }
            const moveEnd = (map as any)?.__moveEnd;
            const zoomEnd = (map as any)?.__zoomEnd;
            if (moveEnd) {
                map.off('moveend', moveEnd);
            }
            if (zoomEnd) {
                map.off('zoomend', zoomEnd);
            }
            (map as any).__moveEnd = null;
            (map as any).__zoomEnd = null;
            const throttledRenderMarkers = (map as any).__throttledRenderMarkers;
            if (throttledRenderMarkers) {
                map.off('move', throttledRenderMarkers);
                map.off('zoom', throttledRenderMarkers);
                map.off('rotate', throttledRenderMarkers);
                map.off('pitch', throttledRenderMarkers);
                map.off('resize', throttledRenderMarkers);
                (map as any).__throttledRenderMarkers = null;
            }
            const assetMarkerHover = (map as any).__assetHoverBound;
            if (assetMarkerHover) {
                map.off('mouseenter', ASSET_MARKERS_LAYER_ID, assetMarkerHover.onEnter);
                map.off('mouseleave', ASSET_MARKERS_LAYER_ID, assetMarkerHover.onLeave);
                map.off('mousemove', ASSET_MARKERS_LAYER_ID, assetMarkerHover.onMove);
                (map as any).__assetHoverBound = null;
            }
            removeSnailTrailLayers(map);
            removeTrafficLayer(map);
            removeGeofenceLayers(map);
            cancelGeofenceDrawingAndCleanup(map);
            cancelVideoDrawingAndCleanup(map);
            removeHeatmapLayers(map);
            map.off('movestart', onStart);
            map.off('idle', onIdle);
            map.off('dragstart', start);
            map.off('zoomstart', start);
            map.off('rotatestart', start);
            map.off('pitchstart', start);
            map.off('idle', stop);
            map.remove();
        };
    }, []);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        safelyInteractWithMap(map, () => {
            renderMarkerLayers(map);
            const featureCount = markersGeojson.features.length;
            if (
                featureCount > 1 &&
                shouldZoomToRef.current &&
                !userIsInteractingRef.current
            ) {
                zoomTo();
                shouldZoomToRef.current = false;
            }
        });
    }, [markersGeojson, selectedDate]);
    useEffect(() => {
        if (selectedSnailTrailAssetId) {
            const imei = assetMarkers.find(am => am.asset_id === selectedSnailTrailAssetId)?.imei;
            if (!imei) {
                return;
            }
            dispatch(fetchSnailTrailRequest({
                date: dayjs(selectedDate ?? dayjs()).format('YYYY-MM-DD'),
                imei,
                slider_unit_type: SliderUnitType.Seconds,
                slider_unit_size: 10,
            }));
        }
    }, [selectedDateRaw])
    useEffect(() => {
        if (!focusAsset) {
            return;
        }
        const map = mapRef.current;
        if (!map) {
            return;
        }
        if (selectedSnailTrailAssetId && focusAsset !== selectedSnailTrailAssetId) {
            dispatch(resetSnailTrail());
            setSelectedSnailTrailAssetId(null);
            removeSnailTrailLayers(map);
        }
        jumpTo([focusAsset]);
        mapAlreadyInMotionRef.current = true;
        dispatch(setFocusAsset(null));
    }, [focusAsset]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        if (checkedAssetsForCompare.length > 1) {
            dispatch(resetSnailTrail());
            setSelectedSnailTrailAssetId(null);
            removeSnailTrailLayers(map);
        }
    }, [checkedAssetsForCompare]);
    useEffect(() => {
        const newAssetMarkerLocationsById = new Map<number, [number, number]>();
        for (const am of assetMarkers) {
            const lng = am.lng as number;
            const lat = am.lat as number;
            if (isValidLngLat(lng, lat)) {
                newAssetMarkerLocationsById.set(am.asset_id, [lng, lat]);
            }
        }
        assetMarkersLngLatByIdRef.current = newAssetMarkerLocationsById;
    }, [assetMarkers]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        safelyInteractWithMap(map, () => {
            if (!selectedSnailTrail || !selectedSnailTrail.length) {
                const existingSource = map.getSource(SNAIL_TRAIL_LINE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
                if (existingSource) {
                    existingSource.setData({ 
                        type: 'FeatureCollection', 
                        features: [], 
                    } as any);
                }
                removeSnailTrailLayers(map);
                return;
            }
            renderSnailTrailLayers(map);
            if (!(map as any).__snailTrailDirectionalArrowZoomHandler) {
                const onZoom = () => renderSnailTrailLayers(map);
                map.on('zoom', onZoom);
                (map as any).__snailTrailDirectionalArrowZoomHandler = onZoom;
            }
            const coords = selectedSnailTrail.map((p: SnailTrailPoint) => [parseFloat(p.lng), parseFloat(p.lat)] as [number, number]);
            if (coords.length >= 2) {
                const bounds = new maplibregl.LngLatBounds(coords[0] as LngLatLike, coords[0] as LngLatLike);
                for (let i = 1; i < coords.length; i++) {
                    bounds.extend(coords[i] as LngLatLike);
                }
                const camera = map.cameraForBounds(bounds, { 
                    padding: MAP_CAMERA_DEFAULT_PADDING, 
                    maxZoom: FIT_BOUNDS_MAX_ZOOM, 
                });
                if (!camera) {
                    return;
                }
                setTimeout(() => {
                    map.stop();
                    map.flyTo({
                        ...camera,
                        duration: FIT_BOUNDS_DEFAULT_DURATION,
                        essential: true,
                        curve: 1.6,
                        easing: (t) => t * (2 - t),
                    });
                }, 100);
            } else if (coords.length === 1) {
                map.easeTo({ 
                    center: coords[0], 
                    zoom: EASE_TO_DEFAULT_ZOOM, 
                    duration: EASE_TO_DEFAULT_DURATION, 
                    essential: true ,
                });
            }
        });
    }, [selectedSnailTrail]);
    useEffect(() => {
        shouldZoomToRef.current = true;
    }, [mapMode]);
    useEffect(() => {
        shouldZoomToRef.current = true;
    }, [checkedAssets]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        setHoveredAsset(map, injectedHoveredAsset);
    }, [injectedHoveredAsset]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        markersGeojsonRef.current = markersGeojson;
        safelyInteractWithMap(map, () => {
            const id = hoveredAssetIdRef.current;
            if (id == null) {
                return;
            }
            setHoveredAsset(map, id);
            const f = (markersGeojson.features as any[]).find((ff) => Number(ff?.properties?.asset_id) === id);
            const coords = (f?.geometry as GeoJSON.Point)?.coordinates as [number, number] | undefined;
            if (coords && assetMarkerPopupRef.current) {
                assetMarkerPopupRef.current.setLngLat(coords);
            }
        });
    }, [markersGeojson]);
    useEffect(() => {
        setSliderValue(sliderMaxSteps);
    }, [mapMode, selectedSnailTrailAssetId]);
    useLayoutEffect(() => { // Only recalc after DOM has settled
        setTimeout(() => measureTimelineScale(), 350); // Wait for animations done before recalc
    }, [timelineWidth, filterSidebarIsOpen, locationSearchActive]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        safelyInteractWithMap(map, () => {
            renderMarkerLayers(map);
            if (hoveredAssetIdRef.current != null) {
                setHoveredAsset(map, hoveredAssetIdRef.current);
            }
            if (map.getLayer(ASSET_MARKER_HOVER_LAYER_ID)) {
                map.setLayoutProperty(
                    ASSET_MARKER_HOVER_LAYER_ID,
                    'icon-image',
                    ['coalesce',
                        ['get', assetMarkerNamesActive ? 'icon_id_pill' : 'icon_id_ring'],
                        ['get', 'icon_id'],
                    ]
                );
            }
        }); 
    }, [assetMarkerNamesActive]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        safelyInteractWithMap(map, () => {
            setTrafficOverlayLayerVisibility(map, trafficLayerActive);
        });
    }, [trafficLayerActive]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        if (!hasInitializedSatelliteStyleRef.current) {
            hasInitializedSatelliteStyleRef.current = true;
            return;
        }
        safelyInteractWithMap(map, () => {
            const nextStyle = getMapStyle(satelliteModeActive);
            if (!nextStyle) {
                console.error('getMapStyle returned falsy value for satelliteModeActive');
                return;
            }
            try {
                map.setStyle(nextStyle, { diff: false });
            } catch (err) {
                console.error('Failed to set map style', err);
                return;
            }
            map.once('styledata', () => {
                try {
                    renderMapFeatures(map);
                } catch (err) {
                    console.error('Failed to render map features after style change', err);
                }
            });
        });
    }, [satelliteModeActive]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }

        safelyInteractWithMap(map, () => {
            if (geofenceLayerActive) {
                renderGeofenceLayers(map);
                if (shouldZoomToGeofencesRef.current && Array.isArray(geofences) && geofences.length > 0) {
                    const fc = createFeatureCollectionFromGeofences(geofences);
                    const bounds = getBoundsForGeojson(fc as any);
                    if (bounds) {
                        map.fitBounds(bounds, {
                            padding: MAP_CAMERA_DEFAULT_PADDING,
                            maxZoom: FIT_BOUNDS_MAX_ZOOM,
                            duration: FIT_BOUNDS_DEFAULT_DURATION,
                        });
                        shouldZoomToGeofencesRef.current = false;
                    }
                }
            } else {
                removeGeofenceLayers(map);
            }
        });
    }, [geofenceLayerActive, geofences]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        safelyInteractWithMap(map, () => {
            renderHeatmapLayers(map);
        });
    }, [eventTypesHeatmapGeojson]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        const canvas = map.getCanvas();
        if (!canvas) {
            return;
        }
        if (geofenceDrawingToolActive || videoDrawingToolActive) {
            canvas.style.cursor = PENCIL_CURSOR_SVG;
        } else {
            canvas.style.cursor = '';
        }
    }, [geofenceDrawingToolActive, videoDrawingToolActive]);
    useEffect(() => { 
        geofenceDrawingToolActiveRef.current = geofenceDrawingToolActive; 
    }, [geofenceDrawingToolActive]);
    useEffect(() => { 
        videoDrawingToolActiveRef.current = videoDrawingToolActive; 
    }, [videoDrawingToolActive]);
    useEffect(() => { 
        dispatch(setMapSliderValue(sliderValue));
    }, [sliderValue]);

    return (
        <>
            {(geofenceDrawingToolActive || videoDrawingToolActive) && (
                <div
                    className='geofence-drawing-tool-key'
                    style={{
                        top: 96,
                        right: 8,
                    }}
                >
                    <div className='geofence-drawing-tool-key_title'>
                        {geofenceDrawingToolActive ? 'Drawing Geo-fence...' : 'Drawing Video Search Area...'}
                    </div>
                    <ul className='geofence-drawing-tool-key_help'>
                        <li>Click to add points</li>
                        <li>Close the shape to <b>search</b></li>
                    </ul>
                </div>
            )}
            <div
                className="map-component"
                style={{
                    width: `calc(100% - ${filterSidebarWidth})`,
                    marginLeft: filterSidebarWidth,
                    height: `calc(100vh - ${FILTER_SIDEBAR_HEIGHT})`,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width 0.3s ease-in-out',
                }}
            >
                {toolbarEnabled && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            background: '#fff',
                            marginLeft: 8,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                height: 32,
                            }}
                        >
                            {mapRef.current ? getToolbarButtonProps(mapRef.current).map((item, idx, array) => {
                                if (item.key !== MapToolbarButtonType.ToggleLocationSearch) {
                                    if (item.key === MapToolbarButtonType.HeatmapsMenu) {
                                        return (
                                            <Tooltip
                                                title={item.tooltip}
                                                key={item.key}
                                            >
                                                <Dropdown
                                                    trigger={['hover']}
                                                    placement="bottomLeft"
                                                    dropdownRender={() => (
                                                        <div
                                                            style={{
                                                                padding: 8,
                                                                background: '#fff',
                                                                borderRadius: 0,
                                                                boxShadow: '0 6px 16px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)',
                                                                minWidth: 240,
                                                                maxHeight: 320,
                                                                overflow: 'auto',
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Tree
                                                                checkable
                                                                defaultExpandAll
                                                                selectable={false}
                                                                treeData={eventTypesHeatmapTreeData}
                                                                style={{ fontSize: '12px' }}
                                                                onCheck={(checkedKeys) => {
                                                                    const raw = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
                                                                    const eventTypeIds = (raw as React.Key[])
                                                                        .filter((k) => k !== EVENT_TYPES_HEATMAP_SELECT_ALL_VALUE)
                                                                        .map((k) => Number(k))
                                                                        .filter((n) => Number.isFinite(n));
                                                                    if (eventTypeIds.length === 0) {
                                                                        dispatch(setDeviceHeatMap(null));
                                                                        return;
                                                                    }
                                                                    if (checkedAssetsForCompare.length > 0) {
                                                                        dispatch(getMapEventsHeatMapRequest({
                                                                            date: selectedDate.format('YYYY-MM-DD'),
                                                                            eventTypes: eventTypeIds,
                                                                            assets: checkedAssetsForCompare,
                                                                        }));
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                >
                                                    <div
                                                        className="btn btn-empty btn-map-toolbar"
                                                        data-testid={`map-button_${item.key}`}
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={(e) => e.preventDefault()}
                                                    >
                                                        {item.icon}
                                                    </div>
                                                </Dropdown>
                                            </Tooltip>
                                        );
                                    }
                                    return (
                                        <Tooltip
                                                title={item.tooltip}
                                                key={item.key}
                                        >
                                            {item.menuOptions?.length ? (
                                                <Dropdown
                                                    trigger={['hover']}
                                                    placement="bottomLeft"
                                                    menu={{
                                                        items: item.menuOptions,
                                                        onClick: ({ key }) => handleToolbarMenuClick(item.key, String(key)),
                                                    }}
                                                >
                                                    <div
                                                        className="btn btn-empty btn-map-toolbar"
                                                        data-testid={`map-button_${item.key}`}
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={(e) => e.preventDefault()}
                                                    >
                                                        {item.icon}
                                                    </div>
                                                </Dropdown>
                                            ) : (
                                                <Button
                                                    className='btn btn-empty btn-map-toolbar'
                                                    style={{ 
                                                        width: 32, 
                                                        height: 32,
                                                    }}
                                                    data-testid={`map-button_${item.key}`}
                                                    onClick={item.onClick ? item.onClick : () => {}}
                                                >
                                                    {item.icon}
                                                </Button>
                                            )}
                                        </Tooltip>
                                    );
                                }
                                return (
                                    <React.Fragment key={item.key}>
                                        <Tooltip title={item.tooltip}>
                                            <Button
                                                className="btn btn-empty btn-map-toolbar"
                                                style={{ 
                                                    width: 32, 
                                                    height: 32,
                                                }}
                                                data-testid={`map-button_${item.key}`}
                                                onClick={item.onClick ? item.onClick : () => {}}
                                            >
                                                {item.icon}
                                            </Button>
                                        </Tooltip>
                                        <LocationSearch
                                            active={locationSearchActive}
                                            width={200}
                                            onPlaceSelected={({ lat, lng }) => {
                                                const map = mapRef.current;
                                                if (!map) {
                                                    return;
                                                }
                                                map.flyTo({
                                                    center: { 
                                                        lng, 
                                                        lat, 
                                                    },
                                                    zoom: 16,
                                                    duration: 450,
                                                    essential: true,
                                                });
                                                map.once('moveend', () => showMapPing(map, lng, lat));
                                            }}
                                            onClose={() => {}}
                                        />
                                    </React.Fragment>
                                );
                            }) : null}
                            <Tooltip title={mapMode === MapMode.Live ? "Show Historic" : "Show Live"}>
                                <Button
                                    className="btn btn-empty map-mode-toggle-btn"
                                    style={{ 
                                        height: 32, 
                                        width: mapMode === MapMode.Live ? 88 : 40,
                                        marginRight: mapMode === MapMode.Live ? 0 : -8,
                                    }}
                                    data-testid={"map-button_toggle-live-mode"}
                                    onClick={toggleMapMode}
                                >
                                    <Time
                                        color={mapMode === MapMode.Live ? "var(--red)" : "var(--cool-gray-60)"}
                                        size="16px"
                                    />
                                    {mapMode === MapMode.Live ? (
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                borderRadius: '50%',
                                                backgroundColor: 'var(--red)',
                                                width: 8,
                                                height: 8,
                                            }}
                                        />
                                    ) : null}
                                    {mapMode === MapMode.Live ? 'Live' : ''}
                                </Button>
                            </Tooltip>
                        </div>
                        <div
                            style={{
                                position: 'relative',
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {selectedSnailTrailAssetId ? (
                                <DeviceTimeline
                                    logicalWidth={timelineWidth}
                                    scaleX={sliderScaleX}
                                    deviceDetails={{
                                        assetId: String(selectedSnailTrailAssetId),
                                        date: selectedDate.format('YYYY-MM-DD'),
                                    }}
                                    onFetchedData={(data: any) => {
                                        setTimelineData(data);
                                        onSetTimelineData(data);
                                    }}
                                />
                            ) : null}
                            <Slider 
                                value={sliderValue}
                                onChange={(val) => {
                                    if (mapMode === MapMode.Live) {
                                        setMapMode(MapMode.Historic);
                                    }
                                    setSliderValue(val);
                                }}
                                min={0}
                                max={sliderMaxSteps}
                                style={{ width: '100%' }} 
                                tooltip={{
                                    open: !parentIsFetchingData,
                                    formatter: formatTooltip,
                                }}
                                styles={{
                                    track: { 
                                        marginTop: '1px',
                                        marginLeft: '1px',
                                    },
                                    tracks: { 
                                        marginTop: '1px',
                                        marginLeft: '1px',
                                    },
                                    rail: { 
                                        marginTop: '1px',
                                        marginLeft: '1px',
                                    },
                                    handle: { 
                                        marginTop: '1px',
                                        marginLeft: '1px',
                                    },
                                }}
                                className={selectedSnailTrailAssetId ? 'snail-trail-slider' : 'ant-slider'}
                            />
                            {selectedSnailTrail?.length ? (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                }}>
                                    
                                </div>
                            ) : null}
                        </div>
                        <div
                            className='date-picker-container'
                            style={{
                                width: 120,
                                minWidth: 120,
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <MemoizedDatePicker
                                value={selectedDate}
                                format="DD/MM/YYYY"
                                onChange={(val) => {
                                    if (val) {
                                        dispatch(setSelectedDate(val));
                                    }
                                }}
                                onFocus={() => setMapMode(MapMode.Historic)}
                                className="date-picker"
                                minDate={dayjs().subtract(telematicsRetentionDays, 'days')}
                                maxDate={dayjs()}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                )}
                <div
                    ref={containerRef}
                    style={{
                        width: '100%',
                        flex: 1,
                    }}
                />
            </div>
        </>
    );
};

export default MapComponent;
